// src/admin/components/EnhancedProductDrawer.tsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  Checkbox,
  Drawer,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Stack,
  Divider,
  Chip,
  InputAdornment,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  AppBar,
  Toolbar,
  Radio,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ToggleButton,
  ToggleButtonGroup,
  Dialog,
  DialogContent,
  DialogActions,
  DialogContentText,
  DialogTitle,
  Tooltip,
  Avatar,
  useTheme,
  Backdrop,
  LinearProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  Upload as UploadIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Warehouse as WarehouseIcon,
  LocalOffer as DiscountIcon,
  Save as SaveIcon,
  CheckCircle as CheckCircleIcon,
  ExpandMore as ExpandMoreIcon,
  Build as BuildIcon,
  AutoAwesome as AutoAwesomeIcon,
  ZoomIn as ZoomInIcon,
  NavigateBefore as NavigateBeforeIcon,
  NavigateNext as NavigateNextIcon,
  Storefront as StorefrontIcon,
  DragIndicator as DragIndicatorIcon,
} from '@mui/icons-material';
import 'quill/dist/quill.snow.css';
import {
  Product,
  ProductFormData,
  Category,
  VariantType,
  ProductVariant,
  VariantSelection,
  VARIANT_CONFIG,
  Warehouse,
  WarehouseAllocation,
  calculateDiscountedPrice,
  calculateDiscountAmount
} from '../../../enums/product';
import { ProductService } from '../product/productService';
import { CategoryService } from '../category/categoryService';
import { VariantService } from '../variant/variantService';
import { WarehouseService } from '../warehouse/warehouseService';
import { Brand, fetchActiveBrands } from '../brand/brandService';
import AddBrandDrawer from '../brand/AddBrandDrawer';
import AddCategoryDrawer from '../category/AddCategoryDrawer';
import { useAuth } from '../../../contexts/AuthContext';
import { useCompany } from '../../../contexts/CompanyContext';
import { getCurrencySymbol } from '../../../util/productUtils';
import { formatNumber } from '../../../util/Formatcurrency';

// ─────────────────────────────────────────────────────────────────────────────
// Rich Text Editor — built directly on quill (no react-quill wrapper)
// ─────────────────────────────────────────────────────────────────────────────
interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  label?: string;
  required?: boolean;
  error?: boolean;
  helperText?: string;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder = 'Enter description...',
  label,
  required = false,
  error = false,
  helperText,
}) => {
  const [focused, setFocused] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const quillInstanceRef = useRef<any>(null);
  const onChangeRef = useRef(onChange);
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);

  useEffect(() => {
    if (!containerRef.current || quillInstanceRef.current) return;

    import('quill').then(({ default: Quill }) => {
      if (!containerRef.current || quillInstanceRef.current) return;

      const toolbarContainer = containerRef.current.querySelector('.ql-custom-toolbar') as HTMLElement;
      const editorContainer = containerRef.current.querySelector('.ql-custom-editor') as HTMLElement;

      const quill = new Quill(editorContainer, {
        theme: 'snow',
        placeholder,
        modules: {
          toolbar: toolbarContainer,
        },
        formats: [
          'header', 'bold', 'italic', 'underline', 'strike',
          'color', 'background', 'list', 'indent',
          'align', 'blockquote', 'code-block', 'link',
        ],
      });

      if (value) {
        quill.root.innerHTML = value;
      }

      quill.on('text-change', () => {
        const html = quill.root.innerHTML;
        onChangeRef.current(html === '<p><br></p>' ? '' : html);
      });

      quill.root.addEventListener('focus', () => setFocused(true));
      quill.root.addEventListener('blur', () => setFocused(false));

      quillInstanceRef.current = quill;
    });

    return () => {
      if (quillInstanceRef.current) {
        quillInstanceRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const quill = quillInstanceRef.current;
    if (!quill) return;
    const currentHtml = quill.root.innerHTML;
    const normalised = currentHtml === '<p><br></p>' ? '' : currentHtml;
    if (normalised !== value) {
      quill.root.innerHTML = value || '';
    }
  }, [value]);

  const borderColor = error
    ? theme.palette.error.main
    : focused
      ? theme.palette.primary.main
      : theme.palette.divider;

  const toolbarBg = theme.palette.background.paper;
  const toolbarBorderColor = theme.palette.divider;
  const toolbarIconColor = theme.palette.text.secondary;
  const toolbarActiveColor = theme.palette.primary.main;
  const editorBg = theme.palette.background.paper;
  const editorTextColor = theme.palette.text.primary;
  const placeholderColor = theme.palette.text.disabled;
  const blockquoteBorderColor = theme.palette.primary.light;
  const blockquoteTextColor = theme.palette.text.secondary;
  const codeBg = isDark ? theme.palette.grey[800] : theme.palette.grey[100];
  const codeTextColor = theme.palette.text.primary;

  return (
    <Box sx={{ width: '100%' }}>
      {label && (
        <Typography
          variant="caption"
          component="label"
          sx={{
            display: 'block',
            mb: 0.5,
            color: error ? 'error.main' : focused ? 'primary.main' : 'text.secondary',
            fontWeight: 500,
            fontSize: '0.75rem',
            transition: 'color 0.2s',
          }}
        >
          {label}
          {required && (
            <Typography component="span" sx={{ color: 'error.main', ml: 0.25 }}>
              *
            </Typography>
          )}
        </Typography>
      )}

      <Box
        ref={containerRef}
        sx={{
          border: '1px solid',
          borderColor,
          borderRadius: 1,
          overflow: 'hidden',
          transition: 'border-color 0.2s, box-shadow 0.2s',
          boxShadow: focused && !error
            ? `0 0 0 2px ${theme.palette.primary.main}20`
            : error && focused
              ? `0 0 0 2px ${theme.palette.error.main}20`
              : 'none',
          '& .ql-toolbar': {
            borderTop: 'none', borderLeft: 'none', borderRight: 'none',
            borderBottom: `1px solid ${toolbarBorderColor}`,
            backgroundColor: toolbarBg, flexWrap: 'wrap',
          },
          '& .ql-toolbar button': { color: toolbarIconColor },
          '& .ql-toolbar button:hover, & .ql-toolbar button.ql-active': { color: toolbarActiveColor },
          '& .ql-toolbar .ql-stroke': { stroke: toolbarIconColor },
          '& .ql-toolbar button:hover .ql-stroke, & .ql-toolbar button.ql-active .ql-stroke': { stroke: toolbarActiveColor },
          '& .ql-toolbar .ql-fill': { fill: toolbarIconColor },
          '& .ql-toolbar button:hover .ql-fill, & .ql-toolbar button.ql-active .ql-fill': { fill: toolbarActiveColor },
          '& .ql-toolbar .ql-picker': { color: toolbarIconColor },
          '& .ql-toolbar .ql-picker-label': { color: toolbarIconColor, borderColor: `${toolbarIconColor}40` },
          '& .ql-toolbar .ql-picker-label:hover': { color: toolbarActiveColor },
          '& .ql-toolbar .ql-picker-options': { backgroundColor: toolbarBg, border: `1px solid ${toolbarBorderColor}`, boxShadow: theme.shadows[4] },
          '& .ql-toolbar .ql-picker-item': { color: editorTextColor },
          '& .ql-toolbar .ql-picker-item:hover': { color: toolbarActiveColor },
          '& .ql-container': { border: 'none', fontFamily: 'inherit', fontSize: '0.875rem', backgroundColor: editorBg },
          '& .ql-editor': {
            minHeight: '220px', maxHeight: '400px', overflowY: 'auto',
            fontFamily: 'inherit', fontSize: '0.875rem', lineHeight: 1.6,
            color: editorTextColor, backgroundColor: editorBg, padding: '10px 14px',
          },
          '& .ql-editor.ql-blank::before': { color: placeholderColor, fontStyle: 'normal', left: '14px' },
          '& .ql-editor h1': { fontSize: '1.5rem', fontWeight: 700, margin: '0 0 8px', color: editorTextColor },
          '& .ql-editor h2': { fontSize: '1.25rem', fontWeight: 600, margin: '0 0 6px', color: editorTextColor },
          '& .ql-editor h3': { fontSize: '1.1rem', fontWeight: 600, margin: '0 0 4px', color: editorTextColor },
          '& .ql-editor ul, & .ql-editor ol': { paddingLeft: '24px' },
          '& .ql-editor li': { color: editorTextColor },
          '& .ql-editor p': { color: editorTextColor },
          '& .ql-editor blockquote': {
            borderLeft: `3px solid ${blockquoteBorderColor}`, paddingLeft: '12px',
            color: blockquoteTextColor, fontStyle: 'italic', margin: '8px 0', backgroundColor: 'transparent',
          },
          '& .ql-editor pre': {
            backgroundColor: codeBg, color: codeTextColor, borderRadius: '4px',
            padding: '8px', fontSize: '0.8rem', fontFamily: 'monospace',
          },
          '& .ql-editor code': {
            backgroundColor: codeBg, color: codeTextColor, borderRadius: '3px',
            padding: '1px 4px', fontSize: '0.8rem', fontFamily: 'monospace',
          },
        }}
      >
        <div className="ql-custom-toolbar ql-toolbar ql-snow">
          <span className="ql-formats">
            <select className="ql-header" defaultValue="">
              <option value="1">Heading 1</option>
              <option value="2">Heading 2</option>
              <option value="3">Heading 3</option>
              <option value="">Normal</option>
            </select>
          </span>
          <span className="ql-formats">
            <button className="ql-bold" /><button className="ql-italic" />
            <button className="ql-underline" /><button className="ql-strike" />
          </span>
          <span className="ql-formats">
            <select className="ql-color" /><select className="ql-background" />
          </span>
          <span className="ql-formats">
            <button className="ql-list" value="ordered" /><button className="ql-list" value="bullet" />
            <button className="ql-indent" value="-1" /><button className="ql-indent" value="+1" />
          </span>
          <span className="ql-formats"><select className="ql-align" /></span>
          <span className="ql-formats">
            <button className="ql-blockquote" /><button className="ql-code-block" />
          </span>
          <span className="ql-formats"><button className="ql-link" /></span>
          <span className="ql-formats"><button className="ql-clean" /></span>
        </div>
        <div className="ql-custom-editor" />
      </Box>

      {helperText && (
        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, mx: '14px', color: error ? 'error.main' : 'text.secondary' }}>
          {helperText}
        </Typography>
      )}
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Read-only rich text renderer (view mode)
// ─────────────────────────────────────────────────────────────────────────────
interface RichTextViewerProps {
  value: string;
}

const RichTextViewer: React.FC<RichTextViewerProps> = ({ value }) => {
  const viewerTheme = useTheme();
  const viewerIsDark = viewerTheme.palette.mode === 'dark';
  const codeBg = viewerIsDark ? 'rgba(255,255,255,0.06)' : viewerTheme.palette.grey[100];

  if (!value) return <Typography variant="body2" color="text.secondary">No description provided.</Typography>;

  return (
    <Box
      sx={{
        '& h1': { fontSize: '1.5rem', fontWeight: 700, mt: 0, mb: 1, color: 'text.primary' },
        '& h2': { fontSize: '1.25rem', fontWeight: 600, mt: 0, mb: 0.75, color: 'text.primary' },
        '& h3': { fontSize: '1.1rem', fontWeight: 600, mt: 0, mb: 0.5, color: 'text.primary' },
        '& p': { mt: 0, mb: 0.75, fontSize: '0.875rem', lineHeight: 1.6, color: 'text.primary' },
        '& ul, & ol': { pl: 3, mb: 0.75 },
        '& li': { fontSize: '0.875rem', lineHeight: 1.6, color: 'text.primary' },
        '& strong': { fontWeight: 700 },
        '& em': { fontStyle: 'italic' },
        '& u': { textDecoration: 'underline' },
        '& s': { textDecoration: 'line-through' },
        '& blockquote': {
          borderLeft: '3px solid', borderColor: 'primary.light', pl: 2, py: 0.5, my: 1,
          color: 'text.secondary', fontStyle: 'italic', fontSize: '0.875rem',
        },
        '& pre': { backgroundColor: codeBg, borderRadius: '4px', p: 1.5, fontSize: '0.8rem', fontFamily: 'monospace', overflowX: 'auto', my: 1, color: 'text.primary' },
        '& code': { backgroundColor: codeBg, borderRadius: '3px', padding: '1px 4px', fontSize: '0.8rem', fontFamily: 'monospace', color: 'text.primary' },
        '& a': { color: 'primary.main', textDecoration: 'underline' },
        '& table': { width: '100%', borderCollapse: 'collapse', my: 1, fontSize: '0.8rem' },
        '& th, & td': { border: '1px solid', borderColor: 'divider', p: '6px 10px' },
        '& th': { bgcolor: 'action.hover', fontWeight: 600 },
      }}
      dangerouslySetInnerHTML={{ __html: value }}
    />
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Helper
// ─────────────────────────────────────────────────────────────────────────────
const isRichTextEmpty = (html: string): boolean => {
  if (!html) return true;
  return html.replace(/<[^>]*>/g, '').trim().length === 0;
};

// ─────────────────────────────────────────────────────────────────────────────
// IMAGE LIGHTBOX COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
interface ImageLightboxProps {
  images: string[];
  initialIndex: number;
  open: boolean;
  onClose: () => void;
}

const ImageLightbox: React.FC<ImageLightboxProps> = ({ images, initialIndex, open, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  useEffect(() => { setCurrentIndex(initialIndex); }, [initialIndex, open]);

  const handlePrev = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentIndex(prev => (prev - 1 + images.length) % images.length);
  }, [images.length]);

  const handleNext = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentIndex(prev => (prev + 1) % images.length);
  }, [images.length]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!open) return;
      if (e.key === 'ArrowLeft') setCurrentIndex(prev => (prev - 1 + images.length) % images.length);
      if (e.key === 'ArrowRight') setCurrentIndex(prev => (prev + 1) % images.length);
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, images.length, onClose]);

  return (
    <Dialog
      open={open} onClose={onClose} maxWidth={false}
      PaperProps={{ sx: { bgcolor: 'rgba(0,0,0,0.95)', boxShadow: 'none', borderRadius: 2, overflow: 'hidden', maxWidth: '90vw', maxHeight: '90vh' } }}
      BackdropProps={{ sx: { bgcolor: 'rgba(0,0,0,0.85)' } }}
    >
      <DialogContent sx={{ p: 0, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: 300, minHeight: 300 }}>
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute', top: 8, right: 8, zIndex: 10,
            bgcolor: 'rgba(0,0,0,0.55)',
            border: '1px solid rgba(255,255,255,0.2)',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' },
          }}
          size="small"
        >
          <CloseIcon fontSize="small" sx={{ color: '#ffffff' }} />
        </IconButton>
        <Box component="img" src={images[currentIndex]} alt={`Product image ${currentIndex + 1}`} sx={{ maxWidth: '85vw', maxHeight: '80vh', objectFit: 'contain', display: 'block' }} />
        {images.length > 1 && (
          <>
            <IconButton
              onClick={handlePrev}
              sx={{
                position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)',
                bgcolor: 'rgba(0,0,0,0.55)',
                border: '1px solid rgba(255,255,255,0.2)',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' },
              }}
            >
              <NavigateBeforeIcon sx={{ color: '#ffffff', fontSize: 28 }} />
            </IconButton>
            <IconButton
              onClick={handleNext}
              sx={{
                position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                bgcolor: 'rgba(0,0,0,0.55)',
                border: '1px solid rgba(255,255,255,0.2)',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' },
              }}
            >
              <NavigateNextIcon sx={{ color: '#ffffff', fontSize: 28 }} />
            </IconButton>
          </>
        )}
        {images.length > 1 && (
          <Box sx={{ position: 'absolute', bottom: 12, left: '50%', transform: 'translateX(-50%)', bgcolor: 'rgba(0,0,0,0.6)', color: 'white', px: 2, py: 0.5, borderRadius: 10, fontSize: '0.75rem' }}>
            {currentIndex + 1} / {images.length}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// WAREHOUSE TABLE — defined OUTSIDE the main component so its identity is
// stable across renders. This prevents React from unmounting/remounting the
// table (and its inputs) every time parent state changes, which was causing
// the focus-loss bug when typing in Qty / Min / Max fields.
// ─────────────────────────────────────────────────────────────────────────────
interface WarehouseTableProps {
  allocations: WarehouseAllocation[];
  onUpdate: (index: number, field: keyof WarehouseAllocation, value: string | number) => void;
  onRemove: (index: number) => void;
  isVariant?: boolean;
  activeWarehouses: Warehouse[];
  subtleBg: string;
  // When set, this row's stock is tracked by individual serial number instead
  // of a plain quantity — Qty becomes read-only, derived from the list below.
  requiresSerialTracking?: boolean;
  onUpdateSerials?: (index: number, serials: string[]) => void;
}

const WarehouseTable: React.FC<WarehouseTableProps> = ({
  allocations,
  onUpdate,
  onRemove,
  activeWarehouses,
  subtleBg,
  requiresSerialTracking,
  onUpdateSerials,
}) => {
  const getAvailable = (allocatedIds: string[], currentIndex: number): Warehouse[] =>
    activeWarehouses.filter(w => !allocatedIds.filter((_, i) => i !== currentIndex).includes(w.id!));

  const [serialDialogIndex, setSerialDialogIndex] = useState<number | null>(null);
  const [serialInput, setSerialInput] = useState('');

  const handleAddSerial = () => {
    if (serialDialogIndex === null) return;
    const value = serialInput.trim();
    if (!value) return;
    const current = allocations[serialDialogIndex].serialNumbers || [];
    if (current.some(s => s.toLowerCase() === value.toLowerCase())) {
      setSerialInput('');
      return;
    }
    onUpdateSerials?.(serialDialogIndex, [...current, value]);
    setSerialInput('');
  };

  const handleRemoveSerial = (serial: string) => {
    if (serialDialogIndex === null) return;
    const current = allocations[serialDialogIndex].serialNumbers || [];
    onUpdateSerials?.(serialDialogIndex, current.filter(s => s !== serial));
  };

  return (
    <>
    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1.5 }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: subtleBg }}>
            <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Warehouse</TableCell>
            <TableCell width={130} sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Qty</TableCell>
            <TableCell width={120} sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Min</TableCell>
            <TableCell width={120} sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Max</TableCell>
            <TableCell width={70} sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Status</TableCell>
            <TableCell width={50}></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {allocations.map((allocation, index) => {
            const allocatedIds = allocations.map(a => a.warehouseId);
            const available = getAvailable(allocatedIds, index);
            return (
              <TableRow key={index}>
                <TableCell>
                  <FormControl fullWidth size="small">
                    <Select
                      value={allocation.warehouseId}
                      onChange={(e) => onUpdate(index, 'warehouseId', e.target.value)}
                      sx={{ fontSize: '0.8rem' }}
                    >
                      {!available.find(w => w.id === allocation.warehouseId) && (
                        <MenuItem value={allocation.warehouseId}>
                          {allocation.warehouseName || 'Unknown'}
                        </MenuItem>
                      )}
                      {available.map(warehouse => (
                        <MenuItem key={warehouse.id} value={warehouse.id}>
                          {warehouse.name} ({warehouse.code})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </TableCell>
                <TableCell>
                  {requiresSerialTracking ? (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setSerialDialogIndex(index)}
                      sx={{ fontSize: '0.7rem', whiteSpace: 'nowrap' }}
                    >
                      {allocation.serialNumbers?.length || 0} Serials
                    </Button>
                  ) : (
                    <TextField
                      type="number"
                      size="small"
                      value={allocation.quantity}
                      onChange={(e) => onUpdate(index, 'quantity', parseInt(e.target.value) || 0)}
                      inputProps={{ min: 0 }}
                      fullWidth
                      sx={{ '& input': { fontSize: '0.8rem', py: 0.75 } }}
                    />
                  )}
                </TableCell>
                <TableCell>
                  <TextField
                    type="number"
                    size="small"
                    value={allocation.minStockLevel}
                    onChange={(e) => onUpdate(index, 'minStockLevel', parseInt(e.target.value) || 0)}
                    inputProps={{ min: 0 }}
                    fullWidth
                    sx={{ '& input': { fontSize: '0.8rem', py: 0.75 } }}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    type="number"
                    size="small"
                    value={allocation.maxStockLevel}
                    onChange={(e) => onUpdate(index, 'maxStockLevel', parseInt(e.target.value) || 0)}
                    inputProps={{ min: 0 }}
                    fullWidth
                    sx={{ '& input': { fontSize: '0.8rem', py: 0.75 } }}
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={
                      allocation.isOutOfStock
                        ? 'Out'
                        : allocation.isOverStock
                          ? 'Over'
                          : allocation.isLowStock
                            ? 'Low'
                            : 'OK'
                    }
                    size="small"
                    color={
                      allocation.isOutOfStock
                        ? 'error'
                        : allocation.isOverStock
                          ? 'warning'
                          : allocation.isLowStock
                            ? 'warning'
                            : 'success'
                    }
                    sx={{ fontSize: '0.65rem', height: 20 }}
                  />
                </TableCell>
                <TableCell>
                  <IconButton onClick={() => onRemove(index)} size="small" color="error">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>

    {requiresSerialTracking && (
      <Dialog
        open={serialDialogIndex !== null}
        onClose={() => { setSerialDialogIndex(null); setSerialInput(''); }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Manage Serial Numbers</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            size="small"
            label="Serial number"
            value={serialInput}
            onChange={(e) => setSerialInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddSerial(); } }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={handleAddSerial}><AddIcon fontSize="small" /></IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ mt: 1, mb: 2 }}
          />
          <Stack direction="row" flexWrap="wrap" gap={1}>
            {(serialDialogIndex !== null ? allocations[serialDialogIndex]?.serialNumbers || [] : []).map(serial => (
              <Chip key={serial} label={serial} size="small" onDelete={() => handleRemoveSerial(serial)} />
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setSerialDialogIndex(null); setSerialInput(''); }} variant="contained" size="small">Done</Button>
        </DialogActions>
      </Dialog>
    )}
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────
interface EnhancedProductDrawerProps {
  isOpen: boolean;
  mode: 'add' | 'edit' | 'view';
  product: Product | null;
  onClose: () => void;
  onSave: () => void;
  onShowToast: (message: string, severity: 'success' | 'error' | 'warning' | 'info') => void;
}

interface VariantMatrix {
  [variantType: string]: string[];
}

type VariantCreationMode = 'manual' | 'bulk';

interface ConfirmDialogState {
  open: boolean;
  title: string;
  message: React.ReactNode;
  loading: boolean;
  onConfirm: () => Promise<void>;
}

const CONFIRM_DIALOG_CLOSED: ConfirmDialogState = {
  open: false, title: '', message: '', loading: false, onConfirm: async () => { },
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
const EnhancedProductDrawer: React.FC<EnhancedProductDrawerProps> = ({
  isOpen, mode, product, onClose, onSave, onShowToast,
}) => {
  const { currentUser } = useAuth();
  const { companyProfile } = useCompany();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [formData, setFormData] = useState<ProductFormData>({
    name: '', description: '', price: '', discount: '0', isOutOfStock: false,
    categoryId: '', subcategoryId: '', brandId: '', sku: '', images: [],
    variants: [], hasVariants: false, stockQuantity: '0',
    minStockLevel: '10', maxStockLevel: '100', warehouseAllocations: [], unitCost: '0',
    tags: [], requiresSerialTracking: false,
  });
  const [currentTag, setCurrentTag] = useState('');

  const [existingImages, setExistingImages] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeStep, setActiveStep] = useState(0);
  const [nextVariantId, setNextVariantId] = useState(1);
  const [variantCreationMode, setVariantCreationMode] = useState<VariantCreationMode>('manual');

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [lightboxImages, setLightboxImages] = useState<string[]>([]);

  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogState>(CONFIRM_DIALOG_CLOSED);

  const [categories, setCategories] = useState<Category[]>([]);
  const [subcategories, setSubcategories] = useState<Category[]>([]);
  const [variantTypes, setVariantTypes] = useState<VariantType[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [loadingData, setLoadingData] = useState(false);

  // ── Quick-add (Category / Subcategory / Brand) ─────────────────────────────
  // Opens the full AddCategoryDrawer / AddBrandDrawer (same fields + validation
  // as Category/Brand Management) instead of a lightweight inline form.
  const [quickAddType, setQuickAddType] = useState<'category' | 'subcategory' | 'brand' | null>(null);

  const [variantMatrix, setVariantMatrix] = useState<VariantMatrix>({});
  const [selectedVariantTypes, setSelectedVariantTypes] = useState<string[]>([]);

  const companyCurrency = companyProfile?.currency || 'LKR';
  const currencySymbol = getCurrencySymbol(companyCurrency);
  const formatNum = (n: number, decimals = 2) => formatNumber(n, decimals);

  const availableVariantTypes = variantTypes.map(vt => vt.name);
  const activeWarehouses = warehouses.filter(w => w.isActive);

  // ── Theme-aware tokens ────────────────────────────────────────────────────
  const cardBg = theme.palette.background.paper;
  const subtleBg = isDark ? 'rgba(255,255,255,0.04)' : theme.palette.grey[50];
  const subtlerBg = isDark ? 'rgba(255,255,255,0.07)' : theme.palette.grey[100];
  const successBg = isDark ? 'rgba(76,175,80,0.12)' : 'rgba(76,175,80,0.08)';
  const successBorder = isDark ? 'rgba(76,175,80,0.35)' : 'rgba(76,175,80,0.3)';
  const dividerColor = theme.palette.divider;
  const textSecondary = theme.palette.text.secondary;

  const getVariantOptions = (variantTypeName: string) => {
    const vt = variantTypes.find(v => v.name === variantTypeName);
    return vt?.options.filter(o => o.isActive) || [];
  };

  const openLightbox = (images: string[], index: number) => {
    setLightboxImages(images); setLightboxIndex(index); setLightboxOpen(true);
  };

  // ── Confirm dialog helpers ────────────────────────────────────────────────
  const openConfirmDialog = (title: string, message: React.ReactNode, onConfirm: () => Promise<void>) => {
    setConfirmDialog({ open: true, title, message, loading: false, onConfirm });
  };

  const handleConfirmDialogConfirm = async () => {
    setConfirmDialog(prev => ({ ...prev, loading: true }));
    try { await confirmDialog.onConfirm(); }
    finally { setConfirmDialog(CONFIRM_DIALOG_CLOSED); }
  };

  const handleConfirmDialogCancel = () => {
    if (confirmDialog.loading) return;
    setConfirmDialog(CONFIRM_DIALOG_CLOSED);
  };

  // ── Effects ───────────────────────────────────────────────────────────────
  useEffect(() => {
    if (isOpen) {
      loadInitialData();
      if (product && mode === 'view') loadProductWarehouseInventory(product.id!);
    }
  }, [isOpen, product, mode]);

  useEffect(() => {
    if (formData.categoryId) {
      loadSubcategories(formData.categoryId);
    } else {
      setSubcategories([]);
      setFormData(prev => ({ ...prev, subcategoryId: '' }));
    }
  }, [formData.categoryId]);

  useEffect(() => {
    if (isOpen) {
      // FIX: Always reset activeStep to 0 whenever the drawer opens,
      // regardless of whether we're adding or editing a product.
      setActiveStep(0);

      if (product && (mode === 'edit' || mode === 'view')) {
        const hasVariants = product.hasVariants !== undefined
          ? product.hasVariants
          : !!(product.variants && product.variants.length > 0);

        const maxVariantId = product.variants?.reduce((max, variant) => {
          if (variant.id) {
            const n = parseInt(variant.id.replace('var-', ''));
            return Math.max(max, n);
          }
          return max;
        }, 0) || 0;

        setNextVariantId(maxVariantId + 1);
        setFormData({
          name: product.name, description: product.description || '',
          price: product.price.toString(), discount: (product.discount || 0).toString(),
          isOutOfStock: product.isOutOfStock, categoryId: product.categoryId,
          subcategoryId: product.subcategoryId || '', brandId: product.brandId || '',
          sku: product.sku, images: [], variants: product.variants || [],
          hasVariants, stockQuantity: product.stockQuantity?.toString() || '0',
          minStockLevel: product.minStockLevel?.toString() || '10',
          maxStockLevel: product.maxStockLevel?.toString() || '100',
          warehouseAllocations: product.warehouseAllocations || [],
          unitCost: product.unitCost?.toString() || '0',
          tags: product.tags || [],
          requiresSerialTracking: product.requiresSerialTracking || false,
        });
        setExistingImages(product.images || []);
      } else {
        resetForm();
      }
      setError('');
      setCurrentTag('');
    }
  }, [isOpen, product, mode]);

  // ── Data loading ──────────────────────────────────────────────────────────
  const loadProductWarehouseInventory = async (productId: string) => {
    try { await ProductService.getProductWithWarehouseDetails(productId); }
    catch (err) { console.error('Error loading product warehouse inventory:', err); }
  };

  const loadInitialData = async () => {
    try {
      setLoadingData(true);
      const [categoriesData, variantTypesData, warehousesData, brandsData] = await Promise.all([
        CategoryService.getMainCategories(),
        VariantService.getActiveVariantTypes(),
        WarehouseService.getActiveWarehouses(),
        fetchActiveBrands(),
      ]);
      setCategories(categoriesData);
      setVariantTypes(variantTypesData);
      setWarehouses(warehousesData);
      setBrands(brandsData);
    } catch (err: any) {
      console.error('Error loading initial data:', err);
      setError('Failed to load categories, variants, warehouses, or brands');
      onShowToast('Failed to load categories, variants, warehouses, or brands', 'error');
    } finally {
      setLoadingData(false);
    }
  };

  const loadSubcategories = async (categoryId: string) => {
    try {
      const data = await CategoryService.getSubcategories(categoryId);
      setSubcategories(data);
    } catch (err: any) {
      console.error('Error loading subcategories:', err);
      onShowToast('Failed to load subcategories', 'error');
    }
  };

  // ── Quick-add (Category / Subcategory / Brand) ─────────────────────────────
  // Opens AddCategoryDrawer / AddBrandDrawer; once they've created the record,
  // refresh the relevant dropdown and auto-select the new item.
  const openQuickAdd = (type: 'category' | 'subcategory' | 'brand') => setQuickAddType(type);
  const closeQuickAdd = () => setQuickAddType(null);

  const handleCategoryQuickAddCreated = async (categoryId: string, parentId: string) => {
    if (!parentId) {
      const updated = await CategoryService.getMainCategories();
      setCategories(updated);
      setFormData(prev => ({ ...prev, categoryId, subcategoryId: '' }));
    } else {
      const updated = await CategoryService.getSubcategories(parentId);
      setSubcategories(updated);
      setFormData(prev => ({ ...prev, subcategoryId: categoryId }));
    }
  };

  const handleBrandQuickAddCreated = async (brandId: string) => {
    const updated = await fetchActiveBrands();
    setBrands(updated);
    setFormData(prev => ({ ...prev, brandId }));
  };

  const resetForm = () => {
    setFormData({
      name: '', description: '', price: '', discount: '0', isOutOfStock: false,
      categoryId: '', subcategoryId: '', brandId: '', sku: '', images: [],
      variants: [], hasVariants: false, stockQuantity: '0',
      minStockLevel: '10', maxStockLevel: '100', warehouseAllocations: [], unitCost: '0',
      tags: [], requiresSerialTracking: false,
    });
    setExistingImages([]);
    setActiveStep(0);
    setNextVariantId(1);
    setError('');
    setVariantMatrix({});
    setSelectedVariantTypes([]);
    setVariantCreationMode('manual');
    setCurrentTag('');
  };

  // ── Form handlers ─────────────────────────────────────────────────────────
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  // ── Tag helpers (search/discovery tags — matched by search everywhere) ────
  const handleAddTag = () => {
    const value = currentTag.trim();
    if (!value) return;
    if (formData.tags.length >= 15) {
      onShowToast('Maximum 15 tags allowed', 'warning');
      return;
    }
    if (formData.tags.some(t => t.toLowerCase() === value.toLowerCase())) {
      onShowToast('Tag already added', 'warning');
      return;
    }
    setFormData(prev => ({ ...prev, tags: [...prev.tags, value] }));
    setCurrentTag('');
  };

  const handleRemoveTag = (tag: string) => {
    setFormData(prev => ({ ...prev, tags: prev.tags.filter(t => t !== tag) }));
  };

  const handleDescriptionChange = (value: string) => {
    setFormData(prev => ({ ...prev, description: value }));
  };

  const handleSelectChange = (e: any) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSwitchChange = (name: string, value: boolean) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (existingImages.length + formData.images.length + files.length > 5) {
      onShowToast('Maximum 5 images allowed per product', 'warning');
      return;
    }
    setFormData(prev => ({ ...prev, images: [...prev.images, ...files] }));
  };

  const handleRemoveNewImage = (index: number) => {
    openConfirmDialog('Remove Image', 'Are you sure you want to remove this image?', async () => {
      setFormData(prev => ({ ...prev, images: prev.images.filter((_, i) => i !== index) }));
    });
  };

  const handleReorderExistingImages = (fromIndex: number, toIndex: number) => {
    setExistingImages(prev => {
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
  };

  const handleReorderNewImages = (fromIndex: number, toIndex: number) => {
    setFormData(prev => {
      const next = [...prev.images];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return { ...prev, images: next };
    });
  };

  const handleRemoveExistingImage = (imageUrl: string) => {
    if (mode === 'view') return;
    openConfirmDialog('Remove Image', 'Are you sure you want to remove this image? This action cannot be undone.', async () => {
      if (!product?.id) return;
      setLoading(true);
      try {
        await ProductService.removeProductImage(product.id, imageUrl);
        setExistingImages(prev => prev.filter(img => img !== imageUrl));
        onShowToast('Image removed successfully', 'success');
      } catch (err: any) {
        console.error('Error removing image:', err);
        onShowToast('Failed to remove image: ' + (err.message || 'Unknown error'), 'error');
      } finally {
        setLoading(false);
      }
    });
  };

  // ── Warehouse allocation handlers ─────────────────────────────────────────
  const addWarehouseAllocation = () => {
    if (activeWarehouses.length === 0) { onShowToast('No active warehouses available', 'warning'); return; }
    const allocated = formData.warehouseAllocations.map(wa => wa.warehouseId);
    const available = activeWarehouses.filter(w => !allocated.includes(w.id!));
    if (available.length === 0) { onShowToast('All warehouses have been allocated', 'warning'); return; }
    const first = available[0];
    const newAlloc: WarehouseAllocation = {
      warehouseId: first.id!, warehouseName: first.name,
      quantity: 0, minStockLevel: 10, maxStockLevel: 100,
      isLowStock: true, isOutOfStock: true, isOverStock: false,
    };
    setFormData(prev => ({ ...prev, warehouseAllocations: [...prev.warehouseAllocations, newAlloc] }));
  };

  const removeWarehouseAllocation = (index: number) => {
    const alloc = formData.warehouseAllocations[index];
    openConfirmDialog(
      'Remove Warehouse',
      <>Are you sure you want to remove the warehouse allocation for <strong>"{alloc.warehouseName || 'this warehouse'}"</strong>?</>,
      async () => {
        setFormData(prev => ({ ...prev, warehouseAllocations: prev.warehouseAllocations.filter((_, i) => i !== index) }));
      }
    );
  };

  const updateWarehouseAllocation = (index: number, field: keyof WarehouseAllocation, value: string | number) => {
    setFormData(prev => {
      const updated = prev.warehouseAllocations.map((a, i) => {
        if (i !== index) return a;
        const u = { ...a, [field]: value };
        if (field === 'warehouseId') {
          const wh = activeWarehouses.find(w => w.id === value);
          u.warehouseName = wh?.name || '';
        }
        if (field === 'quantity' || field === 'minStockLevel' || field === 'maxStockLevel') {
          const qty = field === 'quantity' ? Number(value) : u.quantity;
          const min = field === 'minStockLevel' ? Number(value) : u.minStockLevel;
          const max = field === 'maxStockLevel' ? Number(value) : u.maxStockLevel;
          // FIX: Proper priority order — Out > Over > Low > OK
          u.isOutOfStock = qty === 0;
          u.isOverStock = qty > 0 && qty >= max;
          u.isLowStock = qty > 0 && qty <= min && qty < max;
        }
        return u;
      });
      const total = updated.reduce((s, a) => s + a.quantity, 0);
      return { ...prev, warehouseAllocations: updated, stockQuantity: total.toString() };
    });
  };

  // Serial-tracked rows: quantity is always derived from the serial list —
  // this is the one path that updates both together for simple products.
  const updateWarehouseAllocationSerials = (index: number, serials: string[]) => {
    setFormData(prev => {
      const updated = prev.warehouseAllocations.map((a, i) => {
        if (i !== index) return a;
        const qty = serials.length;
        return {
          ...a,
          serialNumbers: serials,
          quantity: qty,
          isOutOfStock: qty === 0,
          isOverStock: qty > 0 && qty >= a.maxStockLevel,
          isLowStock: qty > 0 && qty <= a.minStockLevel && qty < a.maxStockLevel,
        };
      });
      const total = updated.reduce((s, a) => s + a.quantity, 0);
      return { ...prev, warehouseAllocations: updated, stockQuantity: total.toString() };
    });
  };

  const addVariantWarehouseAllocation = (variantIndex: number) => {
    const variant = formData.variants[variantIndex];
    const allocated = (variant.warehouseAllocations || []).map(wa => wa.warehouseId);
    const available = activeWarehouses.filter(w => !allocated.includes(w.id!));
    if (available.length === 0) { onShowToast('All warehouses have been allocated for this variant', 'warning'); return; }
    const first = available[0];
    const newAlloc: WarehouseAllocation = {
      warehouseId: first.id!, warehouseName: first.name,
      quantity: 0, minStockLevel: 10, maxStockLevel: 100,
      isLowStock: true, isOutOfStock: true, isOverStock: false,
    };
    setFormData(prev => ({
      ...prev,
      variants: prev.variants.map((v, i) =>
        i === variantIndex ? { ...v, warehouseAllocations: [...(v.warehouseAllocations || []), newAlloc] } : v
      ),
    }));
  };

  const removeVariantWarehouseAllocation = (variantIndex: number, allocationIndex: number) => {
    const alloc = (formData.variants[variantIndex].warehouseAllocations || [])[allocationIndex];
    openConfirmDialog(
      'Remove Warehouse',
      <>Are you sure you want to remove the warehouse allocation for <strong>"{alloc?.warehouseName || 'this warehouse'}"</strong> from this variant?</>,
      async () => {
        setFormData(prev => ({
          ...prev,
          variants: prev.variants.map((v, i) =>
            i === variantIndex
              ? { ...v, warehouseAllocations: (v.warehouseAllocations || []).filter((_, j) => j !== allocationIndex) }
              : v
          ),
        }));
      }
    );
  };

  const updateVariantWarehouseAllocation = (
    variantIndex: number, allocationIndex: number,
    field: keyof WarehouseAllocation, value: string | number
  ) => {
    setFormData(prev => ({
      ...prev,
      variants: prev.variants.map((variant, vIndex) => {
        if (vIndex !== variantIndex) return variant;
        const updatedAllocs = (variant.warehouseAllocations || []).map((a, aIndex) => {
          if (aIndex !== allocationIndex) return a;
          const u = { ...a, [field]: value };
          if (field === 'warehouseId') {
            const wh = activeWarehouses.find(w => w.id === value);
            u.warehouseName = wh?.name || '';
          }
          if (field === 'quantity' || field === 'minStockLevel' || field === 'maxStockLevel') {
            const qty = field === 'quantity' ? Number(value) : u.quantity;
            const min = field === 'minStockLevel' ? Number(value) : u.minStockLevel;
            const max = field === 'maxStockLevel' ? Number(value) : u.maxStockLevel;
            // FIX: Proper priority order — Out > Over > Low > OK
            u.isOutOfStock = qty === 0;
            u.isOverStock = qty > 0 && qty >= max;
            u.isLowStock = qty > 0 && qty <= min && qty < max;
          }
          return u;
        });
        const total = updatedAllocs.reduce((s, a) => s + a.quantity, 0);
        return {
          ...variant, warehouseAllocations: updatedAllocs, stockQuantity: total,
          isOutOfStock: total === 0,
          isOverStock: total > 0 && total >= variant.maxStockLevel,
          isLowStock: total > 0 && total <= variant.minStockLevel && total < variant.maxStockLevel,
        };
      }),
    }));
  };

  // Serial-tracked variant rows: quantity is always derived from the serial list.
  const updateVariantWarehouseAllocationSerials = (
    variantIndex: number, allocationIndex: number, serials: string[]
  ) => {
    setFormData(prev => ({
      ...prev,
      variants: prev.variants.map((variant, vIndex) => {
        if (vIndex !== variantIndex) return variant;
        const updatedAllocs = (variant.warehouseAllocations || []).map((a, aIndex) => {
          if (aIndex !== allocationIndex) return a;
          const qty = serials.length;
          return {
            ...a,
            serialNumbers: serials,
            quantity: qty,
            isOutOfStock: qty === 0,
            isOverStock: qty > 0 && qty >= a.maxStockLevel,
            isLowStock: qty > 0 && qty <= a.minStockLevel && qty < a.maxStockLevel,
          };
        });
        const total = updatedAllocs.reduce((s, a) => s + a.quantity, 0);
        return {
          ...variant, warehouseAllocations: updatedAllocs, stockQuantity: total,
          isOutOfStock: total === 0,
          isOverStock: total > 0 && total >= variant.maxStockLevel,
          isLowStock: total > 0 && total <= variant.minStockLevel && total < variant.maxStockLevel,
        };
      }),
    }));
  };

  // ── Manual variant management ─────────────────────────────────────────────
  const addVariant = () => {
    const id = `var-${nextVariantId}`;
    const newVariant: ProductVariant = {
      id, variantSelections: availableVariantTypes.length > 0
        ? [{ variantType: availableVariantTypes[0], variantValue: '' }] : [],
      price: parseFloat(formData.price) || 0, discount: 0,
      sku: formData.sku ? `${formData.sku}-${nextVariantId}` : id,
      isDefault: formData.variants.length === 0,
      stockQuantity: 0, minStockLevel: 10, maxStockLevel: 100,
      isLowStock: true, isOutOfStock: true, isOverStock: false, warehouseAllocations: [],
      requiresSerialTracking: false,
    };
    setFormData(prev => ({ ...prev, variants: [...prev.variants, newVariant] }));
    setNextVariantId(prev => prev + 1);
  };

  const addVariantSelection = (variantIndex: number) => {
    if (formData.variants[variantIndex].variantSelections.length >= VARIANT_CONFIG.maxVariantSelections) return;
    setFormData(prev => ({
      ...prev,
      variants: prev.variants.map((v, i) =>
        i === variantIndex ? {
          ...v, variantSelections: [...v.variantSelections, {
            variantType: availableVariantTypes.length > 0 ? availableVariantTypes[0] : '',
            variantValue: ''
          }]
        } : v
      ),
    }));
  };

  const removeVariantSelection = (variantIndex: number, selectionIndex: number) => {
    setFormData(prev => ({
      ...prev,
      variants: prev.variants.map((v, i) =>
        i === variantIndex ? { ...v, variantSelections: v.variantSelections.filter((_, j) => j !== selectionIndex) } : v
      ),
    }));
  };

  const updateVariantSelection = (variantIndex: number, selectionIndex: number, field: keyof VariantSelection, value: string) => {
    setFormData(prev => ({
      ...prev,
      variants: prev.variants.map((v, i) =>
        i === variantIndex ? {
          ...v, variantSelections: v.variantSelections.map((s, j) =>
            j === selectionIndex ? { ...s, [field]: value } : s
          )
        } : v
      ),
    }));
  };

  const removeVariant = (variantIndex: number) => {
    const variant = formData.variants[variantIndex];
    const label = variant.variantSelections.map(s => s.variantValue).filter(Boolean).join(' / ') || `Variant #${variantIndex + 1}`;
    openConfirmDialog(
      'Delete Variant',
      <>Are you sure you want to delete <strong>"{label}"</strong>? This action cannot be undone.</>,
      async () => {
        const wasDefault = variant.isDefault;
        setFormData(prev => {
          const remaining = prev.variants.filter((_, i) => i !== variantIndex);
          if (wasDefault && remaining.length > 0) {
            return { ...prev, variants: remaining.map((v, i) => i === 0 ? { ...v, isDefault: true } : v) };
          }
          return { ...prev, variants: remaining };
        });
      }
    );
  };

  const setDefaultVariant = (variantIndex: number) => {
    setFormData(prev => ({
      ...prev,
      variants: prev.variants.map((v, i) => ({ ...v, isDefault: i === variantIndex })),
    }));
  };

  const isDuplicateVariant = (variantSelections: VariantSelection[], currentIndex: number): boolean =>
    formData.variants.some((variant, index) => {
      if (index === currentIndex) return false;
      return variantSelections.every(selection =>
        variant.variantSelections.some(vs =>
          vs.variantType === selection.variantType && vs.variantValue === selection.variantValue
        )
      );
    });

  const updateVariantField = (variantIndex: number, field: keyof ProductVariant, value: string | number | boolean) => {
    setFormData(prev => ({
      ...prev,
      variants: prev.variants.map((variant, i) => {
        if (i !== variantIndex) return variant;
        const updated = { ...variant, [field]: value };
        if (field === 'stockQuantity' || field === 'minStockLevel' || field === 'maxStockLevel') {
          const qty = field === 'stockQuantity' ? Number(value) : variant.stockQuantity;
          const min = field === 'minStockLevel' ? Number(value) : variant.minStockLevel;
          const max = field === 'maxStockLevel' ? Number(value) : variant.maxStockLevel;
          // FIX: Proper priority order — Out > Over > Low > OK
          updated.isOutOfStock = qty === 0;
          updated.isOverStock = qty > 0 && qty >= max;
          updated.isLowStock = qty > 0 && qty <= min && qty < max;
        }
        return updated;
      }),
    }));
  };

  // ── Bulk variant generation ───────────────────────────────────────────────
  const handleVariantTypeSelection = (variantType: string, selected: boolean) => {
    if (selected) {
      if (selectedVariantTypes.length >= VARIANT_CONFIG.maxVariantSelections) {
        onShowToast(`Maximum ${VARIANT_CONFIG.maxVariantSelections} variant types allowed`, 'warning');
        return;
      }
      setSelectedVariantTypes(prev => [...prev, variantType]);
      setVariantMatrix(prev => ({ ...prev, [variantType]: [] }));
    } else {
      setSelectedVariantTypes(prev => prev.filter(t => t !== variantType));
      setVariantMatrix(prev => { const n = { ...prev }; delete n[variantType]; return n; });
    }
  };

  const handleVariantValueToggle = (variantType: string, value: string) => {
    setVariantMatrix(prev => {
      const curr = prev[variantType] || [];
      return { ...prev, [variantType]: curr.includes(value) ? curr.filter(v => v !== value) : [...curr, value] };
    });
  };

  const generateVariantCombinations = () => {
    if (selectedVariantTypes.length === 0) { onShowToast('Please select at least one variant type', 'warning'); return; }
    for (const type of selectedVariantTypes) {
      if (!variantMatrix[type] || variantMatrix[type].length === 0) {
        onShowToast(`Please select at least one value for ${type}`, 'warning'); return;
      }
    }
    const existingKeys = formData.variants.map(v =>
      v.variantSelections.map(s => `${s.variantType}:${s.variantValue}`).sort().join('|')
    );
    const combinations: ProductVariant[] = [];
    const generate = (types: string[], current: VariantSelection[], idx: number) => {
      if (idx === types.length) {
        const key = current.map(s => `${s.variantType}:${s.variantValue}`).sort().join('|');
        if (existingKeys.includes(key)) return;
        const variantId = `var-${nextVariantId + combinations.length}`;
        combinations.push({
          id: variantId, variantSelections: [...current],
          price: parseFloat(formData.price) || 0, discount: 0,
          sku: formData.sku ? `${formData.sku}-${nextVariantId + combinations.length}` : variantId,
          isDefault: formData.variants.length === 0 && combinations.length === 0,
          stockQuantity: 0, minStockLevel: 10, maxStockLevel: 100,
          isLowStock: true, isOutOfStock: true, isOverStock: false, warehouseAllocations: [],
        });
        return;
      }
      for (const value of (variantMatrix[types[idx]] || [])) {
        generate(types, [...current, { variantType: types[idx], variantValue: value }], idx + 1);
      }
    };
    generate(selectedVariantTypes, [], 0);
    if (combinations.length === 0) { onShowToast('No new unique combinations to add', 'warning'); return; }
    setFormData(prev => ({ ...prev, variants: [...prev.variants, ...combinations] }));
    setNextVariantId(prev => prev + combinations.length);
    onShowToast(`Generated ${combinations.length} new variant combinations`, 'success');
  };

  // ── Derived helpers ───────────────────────────────────────────────────────
  const getSimpleProductDiscountedPrice = () =>
    calculateDiscountedPrice(parseFloat(formData.price) || 0, parseFloat(formData.discount) || 0);

  const getVariantDiscountedPrice = (variantIndex: number) => {
    const v = formData.variants[variantIndex];
    return calculateDiscountedPrice(v.price, v.discount);
  };

  const shouldShowPriceAndSkuFields = () => !formData.hasVariants;
  const getImagesStep = () => formData.hasVariants ? 3 : 2;
  const getTitle = () => ({ add: 'Add New Product', edit: 'Edit Product', view: 'Product Details' }[mode] || '');
  const getSelectedBrand = (): Brand | undefined => brands.find(b => b.id === formData.brandId);

  const handleVariantCreationModeChange = (_e: React.MouseEvent<HTMLElement>, newMode: VariantCreationMode) => {
    if (newMode !== null) setVariantCreationMode(newMode);
  };

  // ── Validation ────────────────────────────────────────────────────────────
  const validateForm = (): boolean => {
    if (!formData.name.trim()) { setError('Product name is required'); onShowToast('Product name is required', 'error'); return false; }
    if (isRichTextEmpty(formData.description)) { setError('Description is required'); onShowToast('Description is required', 'error'); return false; }
    if (!formData.hasVariants) {
      if (!formData.price || parseFloat(formData.price) <= 0) { setError('Valid price is required'); onShowToast('Valid price is required', 'error'); return false; }
      if (!formData.sku.trim()) { setError('SKU is required'); onShowToast('SKU is required', 'error'); return false; }
      const discount = parseFloat(formData.discount) || 0;
      if (discount < 0 || discount > 100) { setError('Discount must be between 0 and 100'); onShowToast('Discount must be between 0 and 100', 'error'); return false; }
      if (formData.warehouseAllocations.length === 0) { setError('At least one warehouse allocation is required'); onShowToast('At least one warehouse allocation is required', 'error'); return false; }
      const wids = formData.warehouseAllocations.map(a => a.warehouseId);
      if (new Set(wids).size !== wids.length) { setError('Duplicate warehouse allocations detected'); onShowToast('Duplicate warehouse allocations detected', 'error'); return false; }
    }
    if (!formData.categoryId) { setError('Category is required'); onShowToast('Category is required', 'error'); return false; }
    if (parseFloat(formData.minStockLevel) < 0) { setError('Minimum stock level cannot be negative'); onShowToast('Minimum stock level cannot be negative', 'error'); return false; }
    if (parseFloat(formData.maxStockLevel) < parseFloat(formData.minStockLevel)) { setError('Maximum stock level cannot be less than minimum stock level'); onShowToast('Maximum stock level cannot be less than minimum stock level', 'error'); return false; }
    for (let i = 0; i < formData.variants.length; i++) {
      const v = formData.variants[i];
      if (isDuplicateVariant(v.variantSelections, i)) { setError('Duplicate variant combination found.'); onShowToast('Duplicate variant combination found.', 'error'); return false; }
      for (const sel of v.variantSelections) {
        if (!sel.variantValue.trim()) { setError('All variant selections must have values'); onShowToast('All variant selections must have values', 'error'); return false; }
      }
      const vDiscount = v.discount || 0;
      if (vDiscount < 0 || vDiscount > 100) { setError(`Variant #${i + 1} discount must be between 0 and 100`); onShowToast(`Variant #${i + 1} discount must be between 0 and 100`, 'error'); return false; }
      if (v.price < 0) { setError('Variant price cannot be negative'); onShowToast('Variant price cannot be negative', 'error'); return false; }
      if (!v.sku.trim()) { setError('Variant SKU is required'); onShowToast('Variant SKU is required', 'error'); return false; }
    }
    return true;
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (mode === 'view') return;
    if (!validateForm()) return;
    try {
      setLoading(true); setError('');
      if (mode === 'add') {
        await ProductService.createProduct(formData, currentUser?.uid || '');
        onShowToast('Product created successfully', 'success');
      } else if (mode === 'edit' && product?.id) {
        await ProductService.updateProduct(product.id, formData, existingImages);
        onShowToast('Product updated successfully', 'success');
      }
      onSave();
    } catch (err: any) {
      console.error('Error saving product:', err);
      setError(err.message || 'Failed to save product');
      onShowToast(err.message || 'Failed to save product', 'error');
    } finally {
      setLoading(false);
    }
  };

  // ── Image grid (defined inside so it can close over openLightbox) ─────────
  // Supports drag-and-drop reordering via native HTML5 DnD when `onReorder` is
  // passed — dragging a thumbnail onto another swaps its position in the list.
  const [draggedImageIndex, setDraggedImageIndex] = useState<number | null>(null);
  const [dragOverImageIndex, setDragOverImageIndex] = useState<number | null>(null);

  const ImageGrid = ({
    images, onRemove, onReorder, readOnly = false,
  }: { images: string[]; objectUrls?: boolean; onRemove?: (index: number) => void; onReorder?: (fromIndex: number, toIndex: number) => void; readOnly?: boolean }) => (
    <Grid container spacing={1.5}>
      {images.map((src, index) => (
        <Grid item xs={6} sm={4} key={index}>
          <Box
            draggable={!readOnly && !!onReorder}
            onDragStart={(e) => {
              if (readOnly || !onReorder) return;
              setDraggedImageIndex(index);
              e.dataTransfer.effectAllowed = 'move';
            }}
            onDragOver={(e) => {
              if (readOnly || !onReorder || draggedImageIndex === null) return;
              e.preventDefault();
              if (dragOverImageIndex !== index) setDragOverImageIndex(index);
            }}
            onDragLeave={() => {
              if (dragOverImageIndex === index) setDragOverImageIndex(null);
            }}
            onDrop={(e) => {
              if (readOnly || !onReorder || draggedImageIndex === null) return;
              e.preventDefault();
              if (draggedImageIndex !== index) onReorder(draggedImageIndex, index);
              setDraggedImageIndex(null);
              setDragOverImageIndex(null);
            }}
            onDragEnd={() => {
              setDraggedImageIndex(null);
              setDragOverImageIndex(null);
            }}
            sx={{
              position: 'relative', borderRadius: 1.5, overflow: 'hidden', aspectRatio: '1',
              bgcolor: subtleBg, border: '1px solid',
              borderColor: dragOverImageIndex === index ? 'primary.main' : dividerColor,
              outline: dragOverImageIndex === index ? '2px solid' : 'none',
              outlineColor: 'primary.main',
              opacity: draggedImageIndex === index ? 0.4 : 1,
              cursor: 'pointer',
              '&:hover .img-overlay': { opacity: 1 },
              '&:hover img': { transform: 'scale(1.04)' },
            }}
            onClick={() => openLightbox(images, index)}
          >
            <Box component="img" src={src} alt={`Image ${index + 1}`} sx={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.2s ease' }} />
            {index === 0 && (
              <Chip
                label="Primary"
                size="small"
                sx={{ position: 'absolute', top: 6, left: 6, height: 20, fontSize: 10, fontWeight: 700, bgcolor: 'rgba(0,0,0,0.65)', color: '#fff' }}
              />
            )}
            <Box
              className="img-overlay"
              sx={{
                position: 'absolute', inset: 0,
                bgcolor: 'rgba(0,0,0,0.5)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1,
                opacity: 0, transition: 'opacity 0.2s ease',
              }}
            >
              {!readOnly && onReorder && (
                <Tooltip title="Drag to reorder">
                  <Box
                    sx={{
                      width: 36, height: 36, borderRadius: '50%',
                      bgcolor: 'rgba(255,255,255,0.95)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                      cursor: 'grab',
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <DragIndicatorIcon sx={{ fontSize: 20, color: '#212121' }} />
                  </Box>
                </Tooltip>
              )}
              <Tooltip title="View">
                <Box
                  sx={{
                    width: 36, height: 36, borderRadius: '50%',
                    bgcolor: 'rgba(255,255,255,0.95)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                    '&:hover': { bgcolor: '#ffffff', transform: 'scale(1.08)' },
                    transition: 'transform 0.15s ease',
                  }}
                >
                  <ZoomInIcon sx={{ fontSize: 20, color: '#212121' }} />
                </Box>
              </Tooltip>
              {!readOnly && onRemove && (
                <Tooltip title="Remove">
                  <Box
                    sx={{
                      width: 36, height: 36, borderRadius: '50%',
                      bgcolor: 'rgba(211,47,47,0.92)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                      '&:hover': { bgcolor: '#c62828', transform: 'scale(1.08)' },
                      transition: 'transform 0.15s ease',
                    }}
                    onClick={(e) => { e.stopPropagation(); onRemove(index); }}
                  >
                    <DeleteIcon sx={{ fontSize: 18, color: '#ffffff' }} />
                  </Box>
                </Tooltip>
              )}
            </Box>
          </Box>
        </Grid>
      ))}
    </Grid>
  );

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <>
      <Drawer
        anchor="right" open={isOpen} onClose={onClose}
        PaperProps={{ sx: { width: { xs: '100%', sm: '90%', md: 900 }, bgcolor: 'background.default' } }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* FIX: Header — removed product SKU/ID display */}
          <Box sx={{ display: 'flex', alignItems: 'center', px: 3, py: 1.5, borderBottom: 1, borderColor: 'divider', minHeight: 60 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>{getTitle()}</Typography>
            </Box>
            <IconButton onClick={onClose} size="small" sx={{ ml: 1 }}><CloseIcon fontSize="small" /></IconButton>
          </Box>

          {/* Content */}
          <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
            {loadingData ? (
              <Box display="flex" alignItems="center" justifyContent="center" sx={{ py: 8 }}>
                <CircularProgress size={32} />
                <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>Loading form data...</Typography>
              </Box>
            ) : (
              <Box component="form" onSubmit={handleSubmit} sx={{ p: 3 }}>
                {error && (
                  <Alert severity="error" sx={{ mb: 3, borderRadius: 1.5 }} onClose={() => setError('')}>{error}</Alert>
                )}

                {mode !== 'view' ? (
                  <Stepper activeStep={activeStep} orientation="vertical">

                    {/* ── STEP 1: Basic Information ── */}
                    <Step>
                      <StepLabel>Basic Information</StepLabel>
                      <StepContent>
                        <Box sx={{ pt: 2, pb: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.08em' }}>
                            Product Type
                          </Typography>
                          <Grid container spacing={1.5} sx={{ mb: 3 }}>
                            {[
                              { value: false, title: 'Simple Product', desc: 'Single product with warehouse-specific inventory tracking', features: ['Single SKU and price', 'Perfect for unique items'] },
                              { value: true, title: 'Product with Variants', desc: 'Multiple combinations with separate inventory for each', features: ['Multiple SKUs (e.g., sizes, colors)', 'Perfect for configurable products'] },
                            ].map(({ value, title, desc, features }) => (
                              <Grid item xs={12} md={6} key={String(value)}>
                                <Card
                                  sx={{
                                    p: 2, cursor: 'pointer', border: '2px solid',
                                    height: '100%',
                                    display: 'flex',
                                    borderColor: formData.hasVariants === value ? 'primary.main' : dividerColor,
                                    bgcolor: formData.hasVariants === value ? (isDark ? 'rgba(77,182,172,0.12)' : 'rgba(77,182,172,0.06)') : cardBg,
                                    borderRadius: 2, transition: 'all 0.15s ease',
                                    '&:hover': { borderColor: 'primary.light', boxShadow: '0 2px 8px rgba(0,0,0,0.12)', transform: 'none' },
                                  }}
                                  onClick={() => handleSwitchChange('hasVariants', value)}
                                >
                                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, width: '100%' }}>
                                    <Radio checked={formData.hasVariants === value} size="small" sx={{ mt: -0.5, p: 0, color: 'primary.main', '&.Mui-checked': { color: 'primary.main' } }} />
                                    <Box sx={{ flex: 1 }}>
                                      <Typography variant="body2" sx={{ fontWeight: 600, color: formData.hasVariants === value ? 'primary.main' : 'text.primary', mb: 0.5 }}>{title}</Typography>
                                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>{desc}</Typography>
                                      <Stack spacing={0.5}>
                                        {features.map(f => (
                                          <Box key={f} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                            <CheckCircleIcon sx={{ fontSize: 13, color: 'success.main' }} />
                                            <Typography variant="caption" color="text.secondary">{f}</Typography>
                                          </Box>
                                        ))}
                                      </Stack>
                                    </Box>
                                  </Box>
                                </Card>
                              </Grid>
                            ))}
                          </Grid>

                          <Grid container spacing={2}>
                            <Grid item xs={12}>
                              <TextField fullWidth label="Product Name *" name="name" value={formData.name} onChange={handleInputChange} placeholder="Enter product name" size="small" />
                            </Grid>
                            <Grid item xs={12}>
                              <RichTextEditor
                                label="Description" required value={formData.description} onChange={handleDescriptionChange}
                                placeholder="Enter product description — supports bold, bullet points, tables, headers, and more..."
                                error={error === 'Description is required'}
                                helperText={error === 'Description is required' ? 'Description is required' : undefined}
                              />
                            </Grid>

                            {shouldShowPriceAndSkuFields() ? (
                              <>
                                <Grid item xs={12} sm={6}>
                                  <TextField fullWidth label={`Price (${currencySymbol}) *`} name="price" type="number" value={formData.price} onChange={handleInputChange} inputProps={{ step: "0.01", min: "0" }} placeholder="0.00" size="small" />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                  <TextField fullWidth label="Discount (%)" name="discount" type="number" value={formData.discount} onChange={handleInputChange} inputProps={{ step: "1", min: "0", max: "100" }} placeholder="0" size="small"
                                    InputProps={{ startAdornment: <InputAdornment position="start"><DiscountIcon color="action" sx={{ fontSize: 18 }} /></InputAdornment> }}
                                  />
                                </Grid>
                                {parseFloat(formData.price) > 0 && parseFloat(formData.discount) > 0 && (
                                  <Grid item xs={12}>
                                    <Box sx={{ p: 1.5, bgcolor: successBg, border: '1px solid', borderColor: successBorder, borderRadius: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                      <DiscountIcon color="success" sx={{ fontSize: 18 }} />
                                      <Box>
                                        <Typography variant="caption" color="text.secondary">After {formData.discount}% discount:</Typography>
                                        <Stack direction="row" spacing={1.5} alignItems="baseline" sx={{ mt: 0.25 }}>
                                          <Typography variant="caption" sx={{ textDecoration: 'line-through', color: 'text.secondary' }}>{currencySymbol} {formatNum(parseFloat(formData.price))}</Typography>
                                          <Typography variant="subtitle2" color="success.main" sx={{ fontWeight: 700 }}>{currencySymbol} {formatNum(getSimpleProductDiscountedPrice())}</Typography>
                                          <Chip label={`Save ${currencySymbol} ${formatNum(calculateDiscountAmount(parseFloat(formData.price), parseFloat(formData.discount)))}`} size="small" color="success" sx={{ height: 18, fontSize: '0.65rem' }} />
                                        </Stack>
                                      </Box>
                                    </Box>
                                  </Grid>
                                )}
                                <Grid item xs={12} sm={6}>
                                  <TextField fullWidth label="SKU *" name="sku" value={formData.sku} onChange={handleInputChange} placeholder="Product SKU" size="small" />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                      fullWidth
                                      label={`Unit Cost (${currencySymbol})`}
                                      name="unitCost"
                                      type="number"
                                      value={formData.unitCost}
                                      onChange={handleInputChange}
                                      inputProps={{ step: "0.01", min: "0" }}
                                      placeholder="0.00"
                                      size="small"
                                      helperText="Purchase / production cost per unit"
                                    />
                                  </Grid>
                              </>
                            ) : (
                              <Grid item xs={12}>
                                <Alert severity="info" sx={{ borderRadius: 1.5 }}>
                                  <Typography variant="body2">Price, discount, and SKU will be managed at the variant level.</Typography>
                                </Alert>
                              </Grid>
                            )}
                          </Grid>

                          <Box sx={{ mt: 3, border: '1px solid', borderColor: dividerColor, borderRadius: 1.5, p: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                              Tags (Max 15)
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                              Keywords shoppers might search for (e.g. "gift", "wedding", "summer"). Matched by search on both the website and admin.
                            </Typography>
                            <TextField
                              fullWidth
                              size="small"
                              placeholder="Enter tag and press Enter"
                              value={currentTag}
                              onChange={(e) => setCurrentTag(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  handleAddTag();
                                }
                              }}
                              disabled={formData.tags.length >= 15}
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <Button onClick={handleAddTag} disabled={!currentTag.trim() || formData.tags.length >= 15} size="small">
                                      Add
                                    </Button>
                                  </InputAdornment>
                                ),
                              }}
                              sx={{ mb: formData.tags.length > 0 ? 2 : 0 }}
                            />
                            {formData.tags.length > 0 && (
                              <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                                {formData.tags.map((tag, index) => (
                                  <Chip key={index} label={tag} onDelete={() => handleRemoveTag(tag)} variant="outlined" color="primary" size="small" />
                                ))}
                              </Stack>
                            )}
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                          <Button onClick={() => setActiveStep(1)} variant="contained" size="small" disabled={!formData.name.trim() || isRichTextEmpty(formData.description)}>
                            Continue
                          </Button>
                        </Box>
                      </StepContent>
                    </Step>

                    {/* ── STEP 2: Category, Brand & Warehouse ── */}
                    <Step>
                      <StepLabel>{formData.hasVariants ? 'Category & Brand' : 'Category, Brand & Warehouse'}</StepLabel>
                      <StepContent>
                        <Box sx={{ pt: 2, pb: 1 }}>
                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <FormControl fullWidth size="small">
                                  <InputLabel>Main Category *</InputLabel>
                                  <Select name="categoryId" value={formData.categoryId} onChange={handleSelectChange} label="Main Category *">
                                    <MenuItem value="">Select Category</MenuItem>
                                    {categories.map(cat => <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>)}
                                  </Select>
                                </FormControl>
                                <Tooltip title="Add new category">
                                  <IconButton size="small" color="primary" onClick={() => openQuickAdd('category')}>
                                    <AddIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Stack>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <FormControl fullWidth size="small" disabled={!formData.categoryId}>
                                  <InputLabel>Subcategory</InputLabel>
                                  <Select name="subcategoryId" value={formData.subcategoryId} onChange={handleSelectChange} label="Subcategory">
                                    <MenuItem value="">None</MenuItem>
                                    {subcategories.map(sub => <MenuItem key={sub.id} value={sub.id}>{sub.name}</MenuItem>)}
                                  </Select>
                                </FormControl>
                                <Tooltip title={formData.categoryId ? 'Add new subcategory' : 'Select a main category first'}>
                                  <span>
                                    <IconButton size="small" color="primary" disabled={!formData.categoryId} onClick={() => openQuickAdd('subcategory')}>
                                      <AddIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </Stack>
                            </Grid>

                            <Grid item xs={12}>
                              <Divider sx={{ my: 0.5 }} />
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5, mb: 1 }}>
                                <StorefrontIcon color="action" sx={{ fontSize: 18 }} />
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Brand</Typography>
                                <Chip label="Optional" size="small" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                              </Box>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <FormControl fullWidth size="small">
                                  <InputLabel>Brand</InputLabel>
                                  <Select
                                    name="brandId" value={formData.brandId} onChange={handleSelectChange} label="Brand"
                                    renderValue={(selected) => {
                                      if (!selected) return <em style={{ color: textSecondary, fontStyle: 'normal' }}>No Brand</em>;
                                      const brand = brands.find(b => b.id === selected);
                                      if (!brand) return selected;
                                      return (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          {brand.imageUrl && <Avatar src={brand.imageUrl} alt={brand.name} sx={{ width: 20, height: 20, borderRadius: 0.5 }} />}
                                          <Typography variant="body2">{brand.name}</Typography>
                                        </Box>
                                      );
                                    }}
                                  >
                                    <MenuItem value=""><em>No Brand</em></MenuItem>
                                    {brands.map(brand => (
                                      <MenuItem key={brand.id} value={brand.id}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                          {brand.imageUrl ? (
                                            <Avatar src={brand.imageUrl} alt={brand.name} sx={{ width: 24, height: 24, borderRadius: 0.75 }} />
                                          ) : (
                                            <Avatar sx={{ width: 24, height: 24, borderRadius: 0.75, bgcolor: subtlerBg }}>
                                              <StorefrontIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                            </Avatar>
                                          )}
                                          <Box>
                                            <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.2 }}>{brand.name}</Typography>
                                            {brand.websiteUrl && <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>{brand.websiteUrl}</Typography>}
                                          </Box>
                                        </Box>
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                                <Tooltip title="Add new brand">
                                  <IconButton size="small" color="primary" onClick={() => openQuickAdd('brand')}>
                                    <AddIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Stack>
                            </Grid>

                            {formData.brandId && getSelectedBrand() && (
                              <Grid item xs={12} sm={6}>
                                <Box sx={{ p: 1.5, bgcolor: subtleBg, border: '1px solid', borderColor: dividerColor, borderRadius: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                  {getSelectedBrand()!.imageUrl ? (
                                    <Avatar src={getSelectedBrand()!.imageUrl} alt={getSelectedBrand()!.name} variant="rounded" sx={{ width: 36, height: 36 }} />
                                  ) : (
                                    <Avatar variant="rounded" sx={{ width: 36, height: 36, bgcolor: subtlerBg }}>
                                      <StorefrontIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                                    </Avatar>
                                  )}
                                  <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{getSelectedBrand()!.name}</Typography>
                                    {getSelectedBrand()!.websiteUrl && <Typography variant="caption" color="text.secondary">{getSelectedBrand()!.websiteUrl}</Typography>}
                                  </Box>
                                  {getSelectedBrand()!.featured && <Chip label="Featured" size="small" color="warning" sx={{ ml: 'auto', height: 18, fontSize: '0.65rem' }} />}
                                </Box>
                              </Grid>
                            )}

                            {brands.length === 0 && (
                              <Grid item xs={12}>
                                <Alert severity="info" sx={{ borderRadius: 1.5 }}>
                                  <Typography variant="body2">No active brands available. You can create brands in the Brands section.</Typography>
                                </Alert>
                              </Grid>
                            )}

                            {!formData.hasVariants && (
                              <>
                                <Grid item xs={12}>
                                  <Divider sx={{ my: 1 }} />
                                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2, mb: 1.5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <WarehouseIcon color="primary" sx={{ fontSize: 18 }} />
                                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Warehouse Allocation</Typography>
                                    </Box>
                                    <Chip label={`Total: ${formData.warehouseAllocations.reduce((s, a) => s + a.quantity, 0)} units`} color="primary" variant="outlined" size="small" />
                                  </Box>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                                    Distribute stock across warehouses. Total quantity is calculated automatically.
                                  </Typography>
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={!!formData.requiresSerialTracking}
                                        onChange={(e) => handleSwitchChange('requiresSerialTracking', e.target.checked)}
                                        size="small"
                                      />
                                    }
                                    label={<Typography variant="body2">Track individual units by serial number</Typography>}
                                    sx={{ mb: 1 }}
                                  />
                                </Grid>

                                {activeWarehouses.length === 0 && (
                                  <Grid item xs={12}>
                                    <Alert severity="warning" sx={{ borderRadius: 1.5 }}>No active warehouses available. Please create warehouses first.</Alert>
                                  </Grid>
                                )}

                                {formData.warehouseAllocations.length > 0 && (
                                  <Grid item xs={12}>
                                    <WarehouseTable
                                      allocations={formData.warehouseAllocations}
                                      onUpdate={updateWarehouseAllocation}
                                      onRemove={removeWarehouseAllocation}
                                      activeWarehouses={activeWarehouses}
                                      subtleBg={subtleBg}
                                      requiresSerialTracking={formData.requiresSerialTracking}
                                      onUpdateSerials={updateWarehouseAllocationSerials}
                                    />
                                  </Grid>
                                )}

                                {activeWarehouses.length > 0 && (
                                  <Grid item xs={12}>
                                    <Button onClick={addWarehouseAllocation} startIcon={<AddIcon />} variant="outlined" size="small" disabled={formData.warehouseAllocations.length >= activeWarehouses.length}>
                                      Add Warehouse
                                    </Button>
                                  </Grid>
                                )}

                                <Grid item xs={12}>
                                  <FormControlLabel
                                    control={<Checkbox name="isOutOfStock" checked={formData.isOutOfStock} onChange={handleInputChange} size="small" />}
                                    label={<Typography variant="body2">Mark as out of stock (override)</Typography>}
                                  />
                                </Grid>
                              </>
                            )}
                          </Grid>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                          <Button onClick={() => setActiveStep(0)} variant="outlined" size="small">Back</Button>
                          <Button onClick={() => setActiveStep(formData.hasVariants ? 2 : getImagesStep())} variant="contained" size="small" disabled={!formData.categoryId}>
                            Continue
                          </Button>
                        </Box>
                      </StepContent>
                    </Step>

                    {/* ── STEP 3: Variants (conditional) ── */}
                    {formData.hasVariants && (
                      <Step>
                        <StepLabel>Product Variants & Warehouse Allocation</StepLabel>
                        <StepContent>
                          <Box sx={{ pt: 2, pb: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Creation Method</Typography>
                            <ToggleButtonGroup
                              value={variantCreationMode} exclusive onChange={handleVariantCreationModeChange} size="small"
                              sx={{
                                mb: 3,
                                '& .MuiToggleButtonGroup-grouped': {
                                  border: `1px solid ${theme.palette.divider} !important`,
                                  borderRadius: '8px !important', mx: 0.5,
                                  '&:first-of-type': { ml: 0 }, '&:last-of-type': { mr: 0 },
                                  bgcolor: 'transparent', color: theme.palette.text.secondary,
                                  '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)', borderColor: `${theme.palette.primary.main} !important` },
                                  '&.Mui-selected': {
                                    bgcolor: isDark ? `${theme.palette.primary.main}25` : `${theme.palette.primary.main}15`,
                                    color: theme.palette.primary.main, borderColor: `${theme.palette.primary.main} !important`,
                                    '&:hover': { bgcolor: isDark ? `${theme.palette.primary.main}35` : `${theme.palette.primary.main}25` },
                                  },
                                },
                              }}
                            >
                              <ToggleButton value="manual">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1 }}>
                                  <BuildIcon sx={{ fontSize: 16 }} />
                                  <Box sx={{ textAlign: 'left' }}>
                                    <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', color: 'inherit' }}>Manual</Typography>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>One by one</Typography>
                                  </Box>
                                </Box>
                              </ToggleButton>
                              <ToggleButton value="bulk">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1 }}>
                                  <AutoAwesomeIcon sx={{ fontSize: 16 }} />
                                  <Box sx={{ textAlign: 'left' }}>
                                    <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', color: 'inherit' }}>Bulk Generate</Typography>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>All combinations</Typography>
                                  </Box>
                                </Box>
                              </ToggleButton>
                            </ToggleButtonGroup>

                            {variantCreationMode === 'manual' ? (
                              <Box>
                                {availableVariantTypes.length === 0 && <Alert severity="warning" sx={{ mb: 2, borderRadius: 1.5 }}>No variant types available. Please create variant types first.</Alert>}
                                {activeWarehouses.length === 0 && <Alert severity="warning" sx={{ mb: 2, borderRadius: 1.5 }}>No active warehouses available. Please create warehouses first.</Alert>}
                                {formData.variants.length === 0 && <Alert severity="info" sx={{ mb: 2, borderRadius: 1.5 }}>No variants added yet. Click "Add Variant" to start.</Alert>}

                                {formData.variants.map((variant, variantIndex) => (
                                  <Paper key={variant.id || variantIndex} sx={{
                                    p: 2.5, mb: 2, border: '1px solid',
                                    borderColor: variant.isDefault ? 'primary.main' : dividerColor,
                                    bgcolor: variant.isDefault ? (isDark ? 'rgba(77,182,172,0.10)' : 'rgba(77,182,172,0.05)') : subtleBg,
                                    borderRadius: 2,
                                  }}>
                                    {/* FIX: Removed "Default" chip and "Set Default" button */}
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                      <Stack direction="row" alignItems="center" spacing={1}>
                                        <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600 }}>Variant #{variantIndex + 1}</Typography>
                                        <Chip label={`ID: ${variant.id}`} size="small" variant="outlined" color="primary" sx={{ fontSize: '0.65rem', height: 18 }} />
                                        <Chip label={`${variant.stockQuantity} units`} size="small" color="info" variant="outlined" sx={{ fontSize: '0.65rem', height: 18 }} />
                                      </Stack>
                                      <IconButton onClick={() => removeVariant(variantIndex)} color="error" size="small"><DeleteIcon fontSize="small" /></IconButton>
                                    </Box>

                                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', mb: 1 }}>
                                      Selections (Max {VARIANT_CONFIG.maxVariantSelections})
                                    </Typography>
                                    <Grid container spacing={1.5} sx={{ mb: 2 }}>
                                      {variant.variantSelections.map((selection, selectionIndex) => (
                                        <React.Fragment key={selectionIndex}>
                                          <Grid item xs={12} sm={5}>
                                            <FormControl fullWidth size="small">
                                              <InputLabel>Type</InputLabel>
                                              <Select value={selection.variantType} onChange={(e) => updateVariantSelection(variantIndex, selectionIndex, 'variantType', e.target.value)} label="Type" disabled={availableVariantTypes.length === 0}>
                                                {availableVariantTypes.map(type => <MenuItem key={type} value={type}>{type}</MenuItem>)}
                                              </Select>
                                            </FormControl>
                                          </Grid>
                                          <Grid item xs={12} sm={5}>
                                            <FormControl fullWidth size="small">
                                              <InputLabel>Value</InputLabel>
                                              <Select value={selection.variantValue} onChange={(e) => updateVariantSelection(variantIndex, selectionIndex, 'variantValue', e.target.value)} label="Value" disabled={!selection.variantType}>
                                                <MenuItem value="">Select Value</MenuItem>
                                                {getVariantOptions(selection.variantType).map(opt => <MenuItem key={opt.id} value={opt.name}>{opt.name}</MenuItem>)}
                                              </Select>
                                            </FormControl>
                                          </Grid>
                                          <Grid item xs={12} sm={2} sx={{ display: 'flex', alignItems: 'center' }}>
                                            {variant.variantSelections.length > 1 && (
                                              <IconButton onClick={() => removeVariantSelection(variantIndex, selectionIndex)} color="error" size="small"><DeleteIcon fontSize="small" /></IconButton>
                                            )}
                                          </Grid>
                                        </React.Fragment>
                                      ))}
                                      {variant.variantSelections.length < VARIANT_CONFIG.maxVariantSelections && availableVariantTypes.length > 0 && (
                                        <Grid item xs={12}>
                                          <Button onClick={() => addVariantSelection(variantIndex)} startIcon={<AddIcon />} variant="outlined" size="small">Add Selection</Button>
                                        </Grid>
                                      )}
                                    </Grid>

                                    <Grid container spacing={1.5} sx={{ mb: 2 }}>
                                      <Grid item xs={12} sm={4}>
                                        <TextField fullWidth label={`Price (${currencySymbol}) *`} type="number" size="small" value={variant.price} onChange={(e) => updateVariantField(variantIndex, 'price', parseFloat(e.target.value) || 0)} inputProps={{ step: "0.01", min: "0" }} />
                                      </Grid>
                                      <Grid item xs={12} sm={4}>
                                        <TextField fullWidth label="Discount (%)" type="number" size="small" value={variant.discount || 0} onChange={(e) => updateVariantField(variantIndex, 'discount', parseFloat(e.target.value) || 0)} inputProps={{ step: "1", min: "0", max: "100" }}
                                          InputProps={{ startAdornment: <InputAdornment position="start"><DiscountIcon color="action" sx={{ fontSize: 16 }} /></InputAdornment> }}
                                        />
                                      </Grid>
                                      <Grid item xs={12} sm={4}>
                                        <TextField fullWidth label="SKU *" size="small" value={variant.sku} onChange={(e) => updateVariantField(variantIndex, 'sku', e.target.value)} />
                                      </Grid>
                                      <Grid item xs={12} sm={4}>
                                          <TextField
                                            fullWidth
                                            label={`Unit Cost (${currencySymbol})`}
                                            type="number"
                                            size="small"
                                            value={variant.unitCost ?? 0}
                                            onChange={(e) => updateVariantField(variantIndex, 'unitCost', parseFloat(e.target.value) || 0)}
                                            inputProps={{ step: "0.01", min: "0" }}
                                            placeholder="0.00"
                                            helperText="Cost per unit"
                                          />
                                        </Grid>
                                      {variant.price > 0 && (variant.discount || 0) > 0 && (
                                        <Grid item xs={12}>
                                          <Box sx={{ p: 1.25, bgcolor: successBg, border: '1px solid', borderColor: successBorder, borderRadius: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <DiscountIcon color="success" sx={{ fontSize: 16 }} />
                                            <Box>
                                              <Typography variant="caption" color="text.secondary">After {variant.discount}% discount: </Typography>
                                              <Typography component="span" variant="caption" sx={{ textDecoration: 'line-through', color: 'text.secondary', mx: 0.5 }}>{currencySymbol} {formatNum(variant.price)}</Typography>
                                              <Typography component="span" variant="caption" color="success.main" sx={{ fontWeight: 700 }}>{currencySymbol} {getVariantDiscountedPrice(variantIndex).toFixed(2)}</Typography>
                                            </Box>
                                          </Box>
                                        </Grid>
                                      )}
                                      <Grid item xs={6}>
                                        <TextField fullWidth label="Min Stock Level" type="number" size="small" value={variant.minStockLevel} onChange={(e) => updateVariantField(variantIndex, 'minStockLevel', parseFloat(e.target.value) || 0)} inputProps={{ min: 0 }} />
                                      </Grid>
                                      <Grid item xs={6}>
                                        <TextField fullWidth label="Max Stock Level" type="number" size="small" value={variant.maxStockLevel} onChange={(e) => updateVariantField(variantIndex, 'maxStockLevel', parseFloat(e.target.value) || 0)} inputProps={{ min: 0 }} />
                                      </Grid>
                                    </Grid>

                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                      <WarehouseIcon color="primary" sx={{ fontSize: 16 }} />
                                      <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Warehouse Allocation</Typography>
                                    </Box>
                                    {variant.warehouseAllocations && variant.warehouseAllocations.length > 0 && (
                                      <Box sx={{ mb: 1.5 }}>
                                        <WarehouseTable
                                          allocations={variant.warehouseAllocations}
                                          onUpdate={(idx, field, val) => updateVariantWarehouseAllocation(variantIndex, idx, field, val)}
                                          onRemove={(idx) => removeVariantWarehouseAllocation(variantIndex, idx)}
                                          isVariant
                                          activeWarehouses={activeWarehouses}
                                          subtleBg={subtleBg}
                                          requiresSerialTracking={variant.requiresSerialTracking}
                                          onUpdateSerials={(idx, serials) => updateVariantWarehouseAllocationSerials(variantIndex, idx, serials)}
                                        />
                                      </Box>
                                    )}
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          checked={!!variant.requiresSerialTracking}
                                          onChange={(e) => updateVariantField(variantIndex, 'requiresSerialTracking', e.target.checked)}
                                          size="small"
                                        />
                                      }
                                      label={<Typography variant="caption">Track individual units by serial number</Typography>}
                                      sx={{ display: 'block', mb: 1 }}
                                    />
                                    <Button onClick={() => addVariantWarehouseAllocation(variantIndex)} startIcon={<AddIcon />} variant="outlined" size="small"
                                      disabled={activeWarehouses.length === 0 || (variant.warehouseAllocations?.length || 0) >= activeWarehouses.length}>
                                      Add Warehouse
                                    </Button>
                                  </Paper>
                                ))}

                                <Button onClick={addVariant} startIcon={<AddIcon />} variant="contained" size="small" disabled={availableVariantTypes.length === 0 || activeWarehouses.length === 0}>
                                  Add Variant Combination
                                </Button>
                              </Box>
                            ) : (
                              /* Bulk Generation UI */
                              <Box>
                                {availableVariantTypes.length === 0 && <Alert severity="warning" sx={{ mb: 2, borderRadius: 1.5 }}>No variant types available.</Alert>}
                                <Card variant="outlined" sx={{ p: 2.5, mb: 3, borderRadius: 2, bgcolor: 'background.paper', backgroundImage: 'none', borderColor: dividerColor }}>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>Step 1: Select Variant Types</Typography>
                                  <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
                                    {availableVariantTypes.map(type => {
                                      const isSelected = selectedVariantTypes.includes(type);
                                      return (
                                        <Chip key={type} label={type} size="small" onClick={() => handleVariantTypeSelection(type, !isSelected)}
                                          sx={isSelected
                                            ? { bgcolor: theme.palette.primary.main, color: '#fff', fontWeight: 600, '&:hover': { bgcolor: theme.palette.primary.dark } }
                                            : { bgcolor: 'transparent', border: `1px solid ${theme.palette.divider}`, color: theme.palette.text.primary, '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)', borderColor: theme.palette.primary.main } }
                                          }
                                        />
                                      );
                                    })}
                                  </Stack>

                                  {selectedVariantTypes.length > 0 && (
                                    <>
                                      <Divider sx={{ my: 2 }} />
                                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>Step 2: Select Values</Typography>
                                      {selectedVariantTypes.map(type => (
                                        <Box key={type} sx={{ mb: 2 }}>
                                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75, fontWeight: 600 }}>{type}</Typography>
                                          <Stack direction="row" flexWrap="wrap" gap={0.75}>
                                            {getVariantOptions(type).map(option => {
                                              const isValueSelected = (variantMatrix[type] || []).includes(option.name);
                                              return (
                                                <Chip key={option.id} label={option.name} size="small" onClick={() => handleVariantValueToggle(type, option.name)}
                                                  sx={isValueSelected
                                                    ? { bgcolor: theme.palette.primary.main, color: '#fff', fontWeight: 600, '&:hover': { bgcolor: theme.palette.primary.dark } }
                                                    : { bgcolor: 'transparent', border: `1px solid ${theme.palette.divider}`, color: theme.palette.text.primary, '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)', borderColor: theme.palette.primary.main } }
                                                  }
                                                />
                                              );
                                            })}
                                          </Stack>
                                        </Box>
                                      ))}
                                      <Divider sx={{ my: 2 }} />
                                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Typography variant="caption" color="text.secondary">
                                          {selectedVariantTypes.every(t => (variantMatrix[t] || []).length > 0)
                                            ? `Will generate ${Object.values(variantMatrix).reduce((acc, vals) => acc * vals.length, 1)} combinations`
                                            : 'Select values for all types'}
                                        </Typography>
                                        <Button onClick={generateVariantCombinations} variant="contained" size="small" startIcon={<AddIcon />}
                                          disabled={!selectedVariantTypes.every(t => (variantMatrix[t] || []).length > 0)}>
                                          Generate
                                        </Button>
                                      </Box>
                                    </>
                                  )}
                                </Card>
                              </Box>
                            )}

                            {/* All Variants Accordion (bulk mode) */}
                            {formData.variants.length > 0 && variantCreationMode === 'bulk' && (
                              <>
                                <Divider sx={{ my: 3 }} />
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>All Variants ({formData.variants.length})</Typography>
                                  <Typography variant="caption" color="text.secondary">Configure pricing & warehouse allocation</Typography>
                                </Box>
                                {formData.variants.map((variant, variantIndex) => (
                                  <Accordion key={variant.id || variantIndex} sx={{ mb: 1, borderRadius: '8px !important', '&:before': { display: 'none' }, border: '1px solid', borderColor: dividerColor }} elevation={0}>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 48, '& .MuiAccordionSummary-content': { my: 1 } }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%', pr: 1 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                          {variant.variantSelections.map(vs => vs.variantValue).join(' / ') || `Variant #${variantIndex + 1}`}
                                        </Typography>
                                        {/* FIX: Removed "Default" chip */}
                                        <Chip label={`${variant.stockQuantity} units`} size="small" color="info" variant="outlined" sx={{ fontSize: '0.65rem', height: 18 }} />
                                        <Box sx={{ flexGrow: 1 }} />
                                        <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); removeVariant(variantIndex); }}>
                                          <DeleteIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                      </Box>
                                    </AccordionSummary>
                                    <AccordionDetails sx={{ pt: 0, px: 2, pb: 2 }}>
                                      <Grid container spacing={1.5}>
                                        <Grid item xs={12} sm={4}>
                                          <TextField fullWidth label={`Price (${currencySymbol}) *`} type="number" size="small" value={variant.price} onChange={(e) => updateVariantField(variantIndex, 'price', parseFloat(e.target.value) || 0)} inputProps={{ step: "0.01", min: "0" }} />
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                          <TextField fullWidth label="Discount (%)" type="number" size="small" value={variant.discount || 0} onChange={(e) => updateVariantField(variantIndex, 'discount', parseFloat(e.target.value) || 0)} inputProps={{ step: "1", min: "0", max: "100" }}
                                            InputProps={{ startAdornment: <InputAdornment position="start"><DiscountIcon color="action" sx={{ fontSize: 16 }} /></InputAdornment> }}
                                          />
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                          <TextField fullWidth label="SKU *" size="small" value={variant.sku} onChange={(e) => updateVariantField(variantIndex, 'sku', e.target.value)} />
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <TextField
                                              fullWidth
                                              label={`Unit Cost (${currencySymbol})`}
                                              type="number"
                                              size="small"
                                              value={variant.unitCost ?? 0}
                                              onChange={(e) => updateVariantField(variantIndex, 'unitCost', parseFloat(e.target.value) || 0)}
                                              inputProps={{ step: "0.01", min: "0" }}
                                              placeholder="0.00"
                                              helperText="Cost per unit"
                                            />
                                          </Grid>
                                        {variant.price > 0 && (variant.discount || 0) > 0 && (
                                          <Grid item xs={12}>
                                            <Box sx={{ p: 1.25, bgcolor: successBg, border: '1px solid', borderColor: successBorder, borderRadius: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                                              <DiscountIcon color="success" sx={{ fontSize: 16 }} />
                                              <Box>
                                                <Typography variant="caption" color="text.secondary">After {variant.discount}% discount: </Typography>
                                                <Typography component="span" variant="caption" sx={{ textDecoration: 'line-through', color: 'text.secondary', mx: 0.5 }}>{currencySymbol} {variant.price.toFixed(2)}</Typography>
                                                <Typography component="span" variant="caption" color="success.main" sx={{ fontWeight: 700 }}>{currencySymbol} {formatNum(getVariantDiscountedPrice(variantIndex))}</Typography>
                                              </Box>
                                            </Box>
                                          </Grid>
                                        )}
                                        <Grid item xs={6}>
                                          <TextField fullWidth label="Min Stock Level" type="number" size="small" value={variant.minStockLevel} onChange={(e) => updateVariantField(variantIndex, 'minStockLevel', parseFloat(e.target.value) || 0)} inputProps={{ min: 0 }} />
                                        </Grid>
                                        <Grid item xs={6}>
                                          <TextField fullWidth label="Max Stock Level" type="number" size="small" value={variant.maxStockLevel} onChange={(e) => updateVariantField(variantIndex, 'maxStockLevel', parseFloat(e.target.value) || 0)} inputProps={{ min: 0 }} />
                                        </Grid>
                                        <Grid item xs={12}>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                            <WarehouseIcon color="primary" sx={{ fontSize: 16 }} />
                                            <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Warehouse Allocation</Typography>
                                          </Box>
                                          {variant.warehouseAllocations && variant.warehouseAllocations.length > 0 && (
                                            <Box sx={{ mb: 1.5 }}>
                                              <WarehouseTable
                                                allocations={variant.warehouseAllocations}
                                                onUpdate={(idx, field, val) => updateVariantWarehouseAllocation(variantIndex, idx, field, val)}
                                                onRemove={(idx) => removeVariantWarehouseAllocation(variantIndex, idx)}
                                                isVariant
                                                activeWarehouses={activeWarehouses}
                                                subtleBg={subtleBg}
                                                requiresSerialTracking={variant.requiresSerialTracking}
                                                onUpdateSerials={(idx, serials) => updateVariantWarehouseAllocationSerials(variantIndex, idx, serials)}
                                              />
                                            </Box>
                                          )}
                                          <FormControlLabel
                                            control={
                                              <Checkbox
                                                checked={!!variant.requiresSerialTracking}
                                                onChange={(e) => updateVariantField(variantIndex, 'requiresSerialTracking', e.target.checked)}
                                                size="small"
                                              />
                                            }
                                            label={<Typography variant="caption">Track individual units by serial number</Typography>}
                                            sx={{ display: 'block', mb: 1 }}
                                          />
                                          {/* FIX: Removed "Set as Default" button from bulk accordion */}
                                          <Button onClick={() => addVariantWarehouseAllocation(variantIndex)} startIcon={<AddIcon />} variant="outlined" size="small"
                                            disabled={activeWarehouses.length === 0 || (variant.warehouseAllocations?.length || 0) >= activeWarehouses.length}>
                                            Add Warehouse
                                          </Button>
                                        </Grid>
                                      </Grid>
                                    </AccordionDetails>
                                  </Accordion>
                                ))}
                              </>
                            )}
                          </Box>
                          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                            <Button onClick={() => setActiveStep(1)} variant="outlined" size="small">Back</Button>
                            <Tooltip
                              title={formData.variants.length === 0 ? 'Add at least one variant before continuing' : ''}
                              arrow
                              disableHoverListener={formData.variants.length > 0}
                            >
                              <span>
                                <Button
                                  onClick={() => setActiveStep(getImagesStep())}
                                  variant="contained"
                                  size="small"
                                  disabled={formData.variants.length === 0}
                                >
                                  Continue to Images
                                </Button>
                              </span>
                            </Tooltip>
                          </Box>
                          {formData.variants.length === 0 && (
                            <Alert severity="warning" sx={{ mt: 1.5, borderRadius: 1.5 }}>
                              You must add at least one variant combination before proceeding to the next step.
                            </Alert>
                          )}
                        </StepContent>
                      </Step>
                    )}

                    {/* ── STEP 4: Images ── */}
                    <Step>
                      <StepLabel>Product Images</StepLabel>
                      <StepContent>
                        <Box sx={{ pt: 2, pb: 1 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                            Upload up to 5 images. Click any image to preview it in full size.
                          </Typography>
                          {existingImages.length > 0 && (
                            <Box sx={{ mb: 3 }}>
                              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', mb: 1.5 }}>
                                Current Images ({existingImages.length})
                              </Typography>
                              <ImageGrid images={existingImages} onRemove={(index) => handleRemoveExistingImage(existingImages[index])} onReorder={handleReorderExistingImages} />
                            </Box>
                          )}
                          {formData.images.length > 0 && (
                            <Box sx={{ mb: 3 }}>
                              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', mb: 1.5 }}>
                                New Images ({formData.images.length})
                              </Typography>
                              <ImageGrid images={formData.images.map(f => URL.createObjectURL(f))} objectUrls onRemove={handleRemoveNewImage} onReorder={handleReorderNewImages} />
                            </Box>
                          )}
                          {(existingImages.length + formData.images.length) < 5 ? (
                            <Button component="label" variant="outlined" startIcon={<UploadIcon />} size="small">
                              Upload Images
                              <input type="file" hidden multiple accept="image/*" onChange={handleImageUpload} />
                            </Button>
                          ) : (
                            <Alert severity="info" sx={{ borderRadius: 1.5 }}>Maximum 5 images reached. Remove existing images to upload new ones.</Alert>
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                          <Button onClick={() => setActiveStep(formData.hasVariants ? 2 : 1)} variant="outlined" size="small">Back</Button>
                          <Button type="submit" variant="contained" size="small" disabled={loading} startIcon={loading ? <CircularProgress size={16} /> : <SaveIcon />}>
                            {loading ? 'Saving...' : mode === 'add' ? 'Create Product' : 'Update Product'}
                          </Button>
                        </Box>
                      </StepContent>
                    </Step>
                  </Stepper>

                ) : (
                  /* ── VIEW MODE ── */
                  <Box>
                    <Card sx={{ mb: 2.5, borderRadius: 2 }} variant="outlined">
                      <Box sx={{ p: 2.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Basic Information</Typography>
                          <Chip label={formData.hasVariants ? 'With Variants' : 'Simple Product'} size="small" color="primary" variant="outlined" />
                        </Box>
                        <Divider sx={{ mb: 2 }} />
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>Product Name</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>{formData.name}</Typography>
                          </Grid>
                          <Grid item xs={12}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.75 }}>Description</Typography>
                            <Box sx={{ p: 2, bgcolor: subtleBg, borderRadius: 1.5, border: '1px solid', borderColor: dividerColor }}>
                              <RichTextViewer value={formData.description} />
                            </Box>
                          </Grid>
                          {formData.tags.length > 0 && (
                            <Grid item xs={12}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.75 }}>Tags</Typography>
                              <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                                {formData.tags.map((tag, index) => (
                                  <Chip key={index} label={tag} size="small" variant="outlined" color="primary" />
                                ))}
                              </Stack>
                            </Grid>
                          )}
                          {!formData.hasVariants && (
                            <>
                              <Grid item xs={12} sm={6}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>Price</Typography>
                                {parseFloat(formData.discount) > 0 ? (
                                  <Stack spacing={0.5}>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                      <Typography variant="caption" sx={{ textDecoration: 'line-through', color: 'text.secondary' }}>{currencySymbol} {formatNum(parseFloat(formData.price))}</Typography>
                                      <Chip label={`${formData.discount}% OFF`} size="small" color="success" sx={{ height: 16, fontSize: '0.65rem' }} />
                                    </Stack>
                                    <Typography variant="body2" sx={{ fontWeight: 700, color: 'success.main' }}>{currencySymbol} {formatNum(getSimpleProductDiscountedPrice())}</Typography>
                                    <Typography variant="caption" color="text.secondary">Save {currencySymbol} {formatNum(calculateDiscountAmount(parseFloat(formData.price), parseFloat(formData.discount)))}</Typography>
                                  </Stack>
                                ) : (
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{currencySymbol}{parseFloat(formData.price).toFixed(2)}</Typography>
                                )}
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>SKU</Typography>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{formData.sku}</Typography>
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>Unit Cost</Typography>
                                <Typography variant="body2">{currencySymbol} {formatNum(parseFloat(formData.unitCost) || 0)}</Typography>
                              </Grid>
                            </>
                          )}
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>Total Stock</Typography>
                            <Typography variant="body2">{formatNum(parseInt(formData.stockQuantity), 0)} units</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>Status</Typography>
                            {/* <Chip
                              label={formData.isOutOfStock || parseInt(formData.stockQuantity) === 0 ? 'Out of Stock' : parseInt(formData.stockQuantity) <= parseInt(formData.minStockLevel) ? 'Low Stock' : 'In Stock'}
                              color={formData.isOutOfStock || parseInt(formData.stockQuantity) === 0 ? 'error' : parseInt(formData.stockQuantity) <= parseInt(formData.minStockLevel) ? 'warning' : 'success'}
                              size="small"
                            /> */}
                            <Chip
                              label={
                                formData.isOutOfStock || parseInt(formData.stockQuantity) === 0
                                  ? 'Out of Stock'
                                  : formData.warehouseAllocations.some(a => a.isLowStock)
                                    ? 'Low Stock'
                                  : formData.warehouseAllocations.some(a => a.isOverStock)
                                  ? 'Over Stock'
                                    : 'In Stock'
                              }
                              color={
                                formData.isOutOfStock || parseInt(formData.stockQuantity) === 0
                                  ? 'error'
                                  : formData.warehouseAllocations.some(a => a.isLowStock)
                                    ? 'warning'
                                    : formData.warehouseAllocations.some(a => a.isOverStock)
                                    ? 'warning'
                                    : 'success'
                              }
                              size="small"
                            />
                          </Grid>
                        </Grid>
                      </Box>
                    </Card>

                    <Card sx={{ mb: 2.5, borderRadius: 2 }} variant="outlined">
                      <Box sx={{ p: 2.5 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Category & Brand</Typography>
                        <Divider sx={{ mb: 2 }} />
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>Main Category</Typography>
                            <Typography variant="body2">{categories.find(c => c.id === formData.categoryId)?.name || 'N/A'}</Typography>
                          </Grid>
                          {formData.subcategoryId && (
                            <Grid item xs={12} sm={6}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>Subcategory</Typography>
                              <Typography variant="body2">{subcategories.find(c => c.id === formData.subcategoryId)?.name || 'N/A'}</Typography>
                            </Grid>
                          )}
                          <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>Brand</Typography>
                            {formData.brandId && brands.find(b => b.id === formData.brandId) ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {brands.find(b => b.id === formData.brandId)!.imageUrl ? (
                                  <Avatar src={brands.find(b => b.id === formData.brandId)!.imageUrl} alt={brands.find(b => b.id === formData.brandId)!.name} variant="rounded" sx={{ width: 28, height: 28 }} />
                                ) : (
                                  <Avatar variant="rounded" sx={{ width: 28, height: 28, bgcolor: subtlerBg }}>
                                    <StorefrontIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                  </Avatar>
                                )}
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>{brands.find(b => b.id === formData.brandId)!.name}</Typography>
                              </Box>
                            ) : (
                              <Typography variant="body2" color="text.secondary">No brand assigned</Typography>
                            )}
                          </Grid>
                        </Grid>
                      </Box>
                    </Card>

                    {!formData.hasVariants && formData.warehouseAllocations && formData.warehouseAllocations.length > 0 && (
                      <Card sx={{ mb: 2.5, borderRadius: 2 }} variant="outlined">
                        <Box sx={{ p: 2.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <WarehouseIcon color="primary" sx={{ fontSize: 18 }} />
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Warehouse Allocation</Typography>
                          </Box>
                          <Divider sx={{ mb: 2 }} />
                          <TableContainer>
                            <Table size="small">
                              <TableHead>
                                <TableRow sx={{ bgcolor: subtleBg }}>
                                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Warehouse</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Qty</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Min</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Max</TableCell>
                                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Status</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {formData.warehouseAllocations.map((allocation, index) => (
                                  <TableRow key={index}>
                                    <TableCell><Typography variant="body2">{allocation.warehouseName || warehouses.find(w => w.id === allocation.warehouseId)?.name || 'Unknown'}</Typography></TableCell>
                                    <TableCell align="right"><Typography variant="body2">{formatNum(allocation.quantity, 0)}</Typography></TableCell>
                                    <TableCell align="right"><Typography variant="body2">{formatNum(allocation.minStockLevel, 0)}</Typography></TableCell>
                                    <TableCell align="right"><Typography variant="body2">{formatNum(allocation.maxStockLevel, 0)}</Typography></TableCell>
                                    <TableCell>
                                      <Chip
                                        label={
                                          allocation.isOutOfStock
                                            ? 'Out'
                                            : allocation.isOverStock
                                              ? 'Over'
                                              : allocation.isLowStock
                                                ? 'Low'
                                                : 'OK'
                                        }
                                        size="small"
                                        color={
                                          allocation.isOutOfStock
                                            ? 'error'
                                            : allocation.isOverStock
                                              ? 'warning'
                                              : allocation.isLowStock
                                                ? 'warning'
                                                : 'success'
                                        }
                                        sx={{ fontSize: '0.65rem', height: 20 }}
                                      />
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Box>
                      </Card>
                    )}

                    {formData.hasVariants && formData.variants.length > 0 && (
                      <Card sx={{ mb: 2.5, borderRadius: 2 }} variant="outlined">
                        <Box sx={{ p: 2.5 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Product Variants ({formData.variants.length})</Typography>
                          <Divider sx={{ mb: 2 }} />
                          {formData.variants.map((variant, index) => (
                            <Paper key={variant.id || index} sx={{ p: 2, mb: 1.5, bgcolor: subtleBg, borderRadius: 1.5, border: '1px solid', borderColor: dividerColor }}>
                              {/* FIX: Removed "Default" chip from view mode variant headers */}
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>Variant #{index + 1}</Typography>
                                <Chip label={`${variant.stockQuantity} units`} size="small" color="info" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                              </Box>
                              <Grid container spacing={1.5}>
                                <Grid item xs={12}>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.75 }}>Selections</Typography>
                                  <Stack direction="row" flexWrap="wrap" gap={0.75}>
                                    {variant.variantSelections.map((sel, idx) => (
                                      <Chip key={idx} label={`${sel.variantType}: ${sel.variantValue}`} size="small" color="primary" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                                    ))}
                                  </Stack>
                                </Grid>
                                <Grid item xs={6}>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>Price</Typography>
                                  {(variant.discount || 0) > 0 ? (
                                    <Stack spacing={0.25}>
                                      <Typography variant="caption" sx={{ textDecoration: 'line-through', color: 'text.secondary' }}>{currencySymbol} {formatNum(variant.price)}</Typography>
                                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>{currencySymbol} {formatNum(getVariantDiscountedPrice(index))}</Typography>
                                    </Stack>
                                  ) : (
                                    <Typography variant="body2">{currencySymbol} {variant.price.toFixed(2)}</Typography>
                                  )}
                                </Grid>
                                <Grid item xs={6}>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>SKU</Typography>
                                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{variant.sku}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>Unit Cost</Typography>
                                  <Typography variant="body2">{currencySymbol} {formatNum(variant.unitCost || 0)}</Typography>
                                </Grid>
                                {variant.warehouseAllocations && variant.warehouseAllocations.length > 0 && (
                                  <Grid item xs={12}>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.75 }}>Warehouse Allocation</Typography>
                                    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1 }}>
                                      <Table size="small">
                                        <TableHead>
                                          <TableRow sx={{ bgcolor: subtlerBg }}>
                                            <TableCell sx={{ fontSize: '0.7rem', fontWeight: 600, py: 0.75 }}>Warehouse</TableCell>
                                            <TableCell align="right" sx={{ fontSize: '0.7rem', fontWeight: 600, py: 0.75 }}>Qty</TableCell>
                                            <TableCell sx={{ fontSize: '0.7rem', fontWeight: 600, py: 0.75 }}>Status</TableCell>
                                          </TableRow>
                                        </TableHead>
                                        <TableBody>
                                          {variant.warehouseAllocations.map((alloc, aIdx) => (
                                            <TableRow key={aIdx}>
                                              <TableCell sx={{ py: 0.75 }}><Typography variant="caption">{alloc.warehouseName || warehouses.find(w => w.id === alloc.warehouseId)?.name || 'Unknown'}</Typography></TableCell>
                                              <TableCell align="right" sx={{ py: 0.75 }}><Typography variant="caption">{formatNum(alloc.quantity, 0)}</Typography></TableCell>
                                              <TableCell sx={{ py: 0.75 }}>
                                                <Chip
                                                  label={
                                                    alloc.isOutOfStock
                                                      ? 'Out'
                                                      : alloc.isOverStock
                                                        ? 'Over'
                                                        : alloc.isLowStock
                                                          ? 'Low'
                                                          : 'OK'
                                                  }
                                                  size="small"
                                                  color={
                                                    alloc.isOutOfStock
                                                      ? 'error'
                                                      : alloc.isOverStock
                                                        ? 'warning'
                                                        : alloc.isLowStock
                                                          ? 'warning'
                                                          : 'success'
                                                  }
                                                  sx={{ fontSize: '0.6rem', height: 18 }}
                                                />
                                              </TableCell>
                                            </TableRow>
                                          ))}
                                        </TableBody>
                                      </Table>
                                    </TableContainer>
                                  </Grid>
                                )}
                              </Grid>
                            </Paper>
                          ))}
                        </Box>
                      </Card>
                    )}

                    {existingImages.length > 0 && (
                      <Card sx={{ mb: 2.5, borderRadius: 2 }} variant="outlined">
                        <Box sx={{ p: 2.5 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Product Images ({existingImages.length})</Typography>
                          <Divider sx={{ mb: 2 }} />
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>Click any image to view in full size</Typography>
                          <ImageGrid images={existingImages} readOnly />
                        </Box>
                      </Card>
                    )}
                  </Box>
                )}
              </Box>
            )}
          </Box>

          {mode === 'view' && (
            <Box sx={{ borderTop: '1px solid', borderColor: 'divider', p: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button onClick={onClose} variant="outlined" size="small">Close</Button>
            </Box>
          )}
        </Box>
      </Drawer>

      <ImageLightbox images={lightboxImages} initialIndex={lightboxIndex} open={lightboxOpen} onClose={() => setLightboxOpen(false)} />

      {/* ── Full-screen saving overlay ─────────────────────────────────────── */}
      <Backdrop
        open={loading}
        sx={{ zIndex: (t) => t.zIndex.drawer + 200, flexDirection: 'column', gap: 3, bgcolor: 'rgba(0,0,0,0.6)' }}
      >
        <Box
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 3,
            px: 5,
            py: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2.5,
            minWidth: 280,
            boxShadow: 24,
          }}
        >
          <CircularProgress size={48} thickness={4} />
          <Box sx={{ width: '100%' }}>
            <LinearProgress sx={{ borderRadius: 1, mb: 1.5 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600, textAlign: 'center', color: 'text.primary' }}>
              {mode === 'add' ? 'Creating Product…' : 'Updating Product…'}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 0.5 }}>
              Please wait, do not close this window
            </Typography>
          </Box>
        </Box>
      </Backdrop>
      {/* ────────────────────────────────────────────────────────────────────── */}

      <Dialog open={confirmDialog.open} onClose={handleConfirmDialogCancel} maxWidth="xs" fullWidth>
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">{confirmDialog.message}</DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleConfirmDialogCancel} disabled={confirmDialog.loading}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleConfirmDialogConfirm} disabled={confirmDialog.loading}
            startIcon={confirmDialog.loading ? <CircularProgress size={16} /> : <DeleteIcon />}>
            {confirmDialog.loading ? 'Removing...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Quick-add Category / Subcategory / Brand ──────────────────────────── */}
      <AddCategoryDrawer
        open={quickAddType === 'category' || quickAddType === 'subcategory'}
        onClose={closeQuickAdd}
        parentCategories={categories}
        defaultParentId={quickAddType === 'subcategory' ? formData.categoryId : undefined}
        onCreated={handleCategoryQuickAddCreated}
        onShowToast={onShowToast}
      />
      <AddBrandDrawer
        open={quickAddType === 'brand'}
        onClose={closeQuickAdd}
        onCreated={handleBrandQuickAddCreated}
        onShowToast={onShowToast}
      />
    </>
  );
};

export default EnhancedProductDrawer;